import React, { memo, Fragment, useState, useEffect, useCallback, useRef } from 'react'
import { Row, Col, Table, Button, Badge, Form as BootstrapForm, Modal } from 'react-bootstrap'
import Card from '../../components/bootstrap/card'
import { sendConsultationRequest, userRbac } from '../../services/UserService'
import Chart from "react-apexcharts"
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Spin, message, DatePicker, Select, Table as AntTable, Radio, AutoComplete, Empty, Dropdown, Popconfirm, Menu, Popover, Checkbox, Switch, Segmented, Tooltip } from 'antd'
import dayjs from 'dayjs'
import moment from 'moment'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  LogoutOutlined,
  SearchOutlined,
  DashboardOutlined,
  FileTextOutlined,
  ProjectOutlined,
  UserOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownOutlined,
  RightOutlined,
  MessageOutlined,
  CloseOutlined
} from "@ant-design/icons"
import {
  GetLatestStdCertificateDataService,
  fileUploadStatusDataService,
  getDashboardReportSummaryData,
  getReportSummaryData,
  GetReturnsDataService,
  getAllAdminIECsService,
  downloadPendencySummary,
  downloadConsolidatedReportService,
  getConsolidatedReportPreviewDataService,
  downloadEdpmsPendencyService,
  downloadDashboardReportService
} from '../../services/FileDataService'
import { getAdvAuthRdnLicReportService } from '../../services/BOEReportService'
import { getReferenceDataService } from '../../services/MasterService'
import { searchTradeData, searchHscodeOnly, searchHscodeAutocomplete } from '../../services/TradeService'
import debounce from 'lodash/debounce'
import { getChartsService, updateChartService, pinChartService } from '../../services/GraphService'
import { uploadFiles, uploadStdCertificateDataService, deleteStdCertificateDataService, deleteReturn, submitForm, updateReturns } from '../../services/UploadService'
import { cleanFilename } from '../../utilities/utils'
import { SERVER_URL } from '../../const'
import StdCertificateInputModal from './upload/ManualDataEntryInputModal'
import ReturnsInputModal from './upload/ReturnsDataEntryInputModal'
import GraphView from './graphs/GraphView'
import PresentGraphs from './graphs/PresentGraphs'
import InconsistencyReport from './reports/InconsistencyReport'
import ConsolidatedReportView from './ConsolidatedReportView'
import DutySavingSummary from './DutySavingSummary'
import CompareIecTable from './CompareIecTable'
import { combineSummaryRows, resolveSummaryBlock, availableModes, leafHeaders, rowLabel, shortIecName, toNum, hasSummaryData } from './utils/compareIecs'
import { STATUS, statusKeyFromRow, formatCount } from '../../theme/tokens'
import '../../theme/theme.css'

const { RangePicker } = DatePicker;

// Utility for formatting numbers
const formatCurrency = (val, currency = 'USD') => {
  if (val === null || val === undefined) return '-';
  const num = Number(val);
  if (isNaN(num)) return '-';
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Compact currency for KPI tiles — abbreviates large values to industry-standard units:
// INR uses the Indian system (Lakh / Crore), USD uses K / M / B. Values below one Lakh
// (INR) or one thousand (USD) are shown in full. ₹0.00 is preserved (not treated as empty).
const formatCurrencyCompact = (val, currency = 'INR') => {
  if (val === null || val === undefined) return '-';
  const num = Number(val);
  if (isNaN(num)) return '-';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const symbol = currency === 'USD' ? '$' : '₹';
  const fmt = (n, min = 2) => n.toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: 2 });

  if (currency === 'USD') {
    if (abs >= 1e9) return `${sign}${symbol}${fmt(abs / 1e9)} B`;
    if (abs >= 1e6) return `${sign}${symbol}${fmt(abs / 1e6)} M`;
    if (abs >= 1e3) return `${sign}${symbol}${fmt(abs / 1e3)} K`;
    return `${sign}${symbol}${fmt(abs)}`;
  }
  // INR — Indian numbering (Lakh = 1e5, Crore = 1e7)
  if (abs >= 1e7) return `${sign}${symbol}${fmt(abs / 1e7)} Cr`;
  if (abs >= 1e5) return `${sign}${symbol}${fmt(abs / 1e5)} L`;
  return `${sign}${symbol}${fmt(abs)}`;
};

// Deep-linkable tab slugs — keeps the active tab in the URL (?tab=graphs) so a refresh or
// shared link lands on the same tab. Only the main sidebar tabs are mapped.
const TAB_SLUGS = { CIP: 'cip', D2D: 'd2d', PRESENT: 'graphs', STANDARD: 'standard' };
const SLUG_TABS = { cip: 'CIP', d2d: 'D2D', graphs: 'PRESENT', standard: 'STANDARD' };

// Dashboard filters (date range, currency, FTA filters) are kept for the browser-tab session so
// they survive tab / sub-tab switches and leaving the dashboard and coming back. Cleared on logout
// by clearStoragePreservingReportColumns().
export const DASH_FILTERS_KEY = 'cipDashFilters'
const readDashFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DASH_FILTERS_KEY)) || {}
  } catch (e) {
    return {}
  }
}
const saveDashFilters = (patch) => {
  try {
    sessionStorage.setItem(DASH_FILTERS_KEY, JSON.stringify({ ...readDashFilters(), ...patch }))
  } catch (e) { }
}

const AlternateDashboard = memo(() => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Authentication & Details
  const [userDetails] = useState({
    role: localStorage.getItem('role'),
    iec: localStorage.getItem('iec_no'),
    name: localStorage.getItem('name')
  })

  const [charts, setCharts] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleDownloadConsolidatedReport = async () => {
    try {
      setDownloadingReport(true);
      message.loading({ content: 'Generating Consolidated Report...', key: 'consolidated_download' });
      const token = localStorage.getItem('token');
      const fromDateStr = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const toDateStr = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;
      const response = await downloadConsolidatedReportService(token, activeIEC, fromDateStr, toDateStr);
      if (response && response.data) {
        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          const json = JSON.parse(text);
          message.error({ content: json.message || 'Failed to generate Consolidated Report.', key: 'consolidated_download' });
          return;
        }

        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        let filename = 'Consolidated_Report.xlsx';
        const disposition = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
        if (disposition && disposition.includes('filename=')) {
          const matches = disposition.match(/filename="?([^"]+)"?/);
          if (matches && matches[1]) {
            filename = matches[1];
          }
        } else {
          const now = new Date();
          const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
          const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
          filename = `Consolidated_Report_${dateStr}_${timeStr}.xlsx`;
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success({ content: 'Consolidated Report downloaded successfully!', key: 'consolidated_download' });
      } else {
        message.error({ content: 'Failed to download report.', key: 'consolidated_download' });
      }
    } catch (error) {
      console.error('Download consolidated report error:', error);
      let errorMsg = 'Failed to download Consolidated Report.';
      if (error.response && error.response.data && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.message) errorMsg = json.message;
        } catch (e) { }
      }
      message.error({ content: errorMsg, key: 'consolidated_download' });
    } finally {
      setDownloadingReport(false);
    }
  };

  const fetchUserCharts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setChartsLoading(true);
    try {
      const response = await getChartsService(token);
      if (response.status === 'success') {
        setCharts(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch user charts:', error);
    } finally {
      setChartsLoading(false);
    }
  }, []);

  const handlePinChart = async (chartId, pageName) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      if (chartId === 'unpin') {
        const currentlyPinned = charts.find(c => c.page_name === pageName && c.show_on_dashboard);
        if (currentlyPinned) {
          await pinChartService(token, currentlyPinned.chart_id, { show_on_dashboard: false, page_name: pageName });
          message.success('Unpinned chart successfully');
        }
      } else {
        await pinChartService(token, chartId, { show_on_dashboard: true, page_name: pageName });
        const selectedChart = charts.find(c => c.chart_id === chartId);
        message.success(`Pinned "${selectedChart?.chart_name || 'Chart'}" to this page`);
      }
      fetchUserCharts();
    } catch (error) {
      message.error('Failed to update pinned chart');
    }
  };

  // Dynamic States
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = SLUG_TABS[searchParams.get('tab')];
    if (fromUrl) return fromUrl;
    const fromStorage = localStorage.getItem('alternateActiveTab');
    if (fromStorage && Object.values(SLUG_TABS).includes(fromStorage)) return fromStorage;
    return 'CIP';
  });
  const [selectedStatementType, setSelectedStatementType] = useState(() => {
    const fromUrl = searchParams.get('compliance') || searchParams.get('statement');
    if (fromUrl) return fromUrl.toUpperCase();
    const fromStorage = localStorage.getItem('selectedStatementType');
    if (fromStorage) return fromStorage;
    return 'EH';
  });
  const [showStatementsModal, setShowStatementsModal] = useState(false)
  const [showCertDataModal, setShowCertDataModal] = useState(false)
  const [showConsolidatedModal, setShowConsolidatedModal] = useState(false)
  const [excelPreviewTab, setExcelPreviewTab] = useState('Summary')
  const [expandedRowsAlt, setExpandedRowsAlt] = useState({ '16': false })

  const toggleRowExpandAlt = (srNo, e) => {
    if (e) e.stopPropagation();
    const key = String(srNo || '');
    setExpandedRowsAlt(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const [showCreateCertForm, setShowCreateCertForm] = useState(false)
  const [newCertForm, setNewCertForm] = useState({
    iec_no: '',
    iec_name: '',
    file_no: '',
    issuing_date: '',
    expiry_date: '',
    issuing_authority: '',
    file: null
  })
  const [selectedModule, setSelectedModule] = useState(null) // null, 'D' (Module D clicked)
  const [selectedLicense, setSelectedLicense] = useState(null)
  const [activeLicTab, setActiveLicTab] = useState('ExpObg') // 'ExpObg', 'ImpSum', 'LicDash'

  const normalizeStatementType = (type) => {
    const map = {
      'ADV_AUTH': 'AdvAuth-EO',
      'BRC': 'EDPMS',
      'EPCG': 'EPCG-Auth-EO',
      'DBK_PENDING': 'DBK-AIR',
      'DBK_SCROLL': 'DBK-AIR',
      'DBK_SBR': 'DBK-AIR',
      'IGST': 'IGST-Refund',
      'RODTEP_SCRIP': 'Rodtep-Util',
      'RODTEP_SCROLL': 'RODTEP-Exp',
      'MANUAL_REMARKS': 'Inconsistency',
      'ISSUED_COO': 'TAT-ImportClearance',
      'FTA': 'FTA-Opportunity'
    };
    return map[type] || type;
  };

  const statementsList = [
    { key: 'IEC', title: 'IEC' },
    { key: 'EH', title: 'EH' },
    { key: 'AEO', title: 'AEO' },
    { key: 'RCMC', title: 'RCMC' },
    { key: 'FSP', title: 'FSP' },
    { key: 'DSC', title: 'DSC' },
    { key: 'Returns', title: 'Returns' }
  ];

  const statementRoutes = {
    'AEO': '/dashboard/upload/upload-files?__card__=AEO',
    'EH': '/dashboard/upload/upload-files?__card__=EH',
    'RCMC': '/dashboard/upload/upload-files?__card__=RCMC',
    'FSP': '/dashboard/upload/upload-files?__card__=FSP',
    'DSC': '/dashboard/upload/upload-files?__card__=DSC',
    'Returns': '/dashboard/upload/upload-files?__card__=Returns'
  };

  const handleSelectStatement = (itemKey) => {
    setSelectedStatementType(itemKey);
    setActiveTab('STANDARD');
  };


  const certModalColumns = [
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <div className="d-flex align-items-center gap-1">
          <button
            className="cip-btn-submit"
            style={{
              borderRadius: '8px',
              width: '26px',
              height: '26px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => handleEditCert(record)}
            title="Edit Certificate"
          >
            <EditOutlined style={{ fontSize: '13px' }} />
          </button>
          <Popconfirm
            title="Delete Certificate"
            description="Are you sure you want to delete this certificate record?"
            onConfirm={() => handleDeleteCert(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <button
              style={{
                backgroundColor: '#dc2626',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                width: '26px',
                height: '26px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="Delete Certificate"
            >
              <DeleteOutlined style={{ fontSize: '13px' }} />
            </button>
          </Popconfirm>
          <button
            className="cip-btn-submit"
            style={{
              borderRadius: '8px',
              width: '26px',
              height: '26px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => {
              const route = statementRoutes[selectedStatementType] || `/dashboard/upload/upload-files?__card__=${selectedStatementType}`;
              navigate(route);
            }}
            title="Upload/Manage Certificates"
          >
            <UploadOutlined style={{ fontSize: '13px' }} />
          </button>
        </div>
      )
    },
    {
      title: 'IEC',
      dataIndex: 'iec_no',
      key: 'iec_no',
      width: 130,
      render: (text) => <span style={{ fontWeight: '600', color: '#111827', fontSize: '15px' }}>{text || activeIEC || '-'}</span>
    },
    {
      title: 'IEC Name',
      dataIndex: 'iec_name',
      key: 'iec_name',
      width: 300,
      render: (text) => <span style={{ fontWeight: '700', color: '#111827', textTransform: 'uppercase', fontSize: '14px' }}>{text || selectedIECName || '-'}</span>
    },
    {
      title: 'File No',
      dataIndex: 'file_no',
      key: 'file_no',
      width: 200,
      render: (text) => <span style={{ fontWeight: '700', color: '#1f2937', fontSize: '15px' }}>{text || '-'}</span>
    },
    {
      title: 'Issuing Date',
      dataIndex: 'issuing_date',
      key: 'issuing_date',
      width: 130,
      render: (text) => <span style={{ color: '#374151', fontSize: '15px', fontWeight: '500' }}>{text || '-'}</span>
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 130,
      render: (text) => <span style={{ color: '#374151', fontSize: '15px', fontWeight: '500' }}>{text || '-'}</span>
    },
    {
      title: 'Issuing Authority',
      dataIndex: 'issuing_authority',
      key: 'issuing_authority',
      width: 280,
      render: (text) => <span style={{ color: '#374151', fontSize: '15px', fontWeight: '500' }}>{text || '-'}</span>
    }
  ];

  const ftaModalColumns = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 70,
      render: (_, record) => (
        <Popconfirm
          title="Delete this file?"
          onConfirm={() => {
            setFtaFileList(prev => prev.filter(item => item.key !== record.key));
            message.success('File deleted successfully');
          }}
        >
          <button
            style={{
              backgroundColor: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              width: '26px',
              height: '26px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            title="Delete"
          >
            <i className="fa-solid fa-trash" style={{ fontSize: '12px' }}></i>
          </button>
        </Popconfirm>
      )
    },
    {
      title: 'File Name',
      dataIndex: 'filename',
      key: 'filename',
      width: 380,
      render: (text) => <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>{text || '-'}</span>
    },
    {
      title: 'User IEC',
      dataIndex: 'user_iec_no',
      key: 'user_iec_no',
      width: 130,
      render: (text) => <span style={{ color: '#475569', fontSize: '15px', fontWeight: '500' }}>{text || activeIEC || '-'}</span>
    },
    {
      title: 'IEC Name',
      dataIndex: 'fullname',
      key: 'fullname',
      width: 280,
      render: (text) => <span style={{ color: '#475569', fontWeight: '600', fontSize: '14px' }}>{text || selectedIECName || '-'}</span>
    },
    {
      title: 'File Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (text) => <span style={{ color: '#16a34a', fontWeight: '700', fontSize: '15px' }}>{text || 'Done'}</span>
    },
    {
      title: 'Remark',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 130,
      render: (text) => <span style={{ color: '#475569', fontSize: '15px', fontWeight: '500' }}>{text || 'Success'}</span>
    },
    {
      title: 'Uploaded on',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (text) => <span style={{ color: '#64748b', fontSize: '15px', fontWeight: '500' }}>{text || '-'}</span>
    }
  ];

  const returnsModalColumns = [
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <div className="d-flex align-items-center gap-1">
          <button
            className="cip-btn-submit"
            style={{
              borderRadius: '8px',
              width: '26px',
              height: '26px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => handleEditCert(record)}
            title="Edit Return"
          >
            <EditOutlined style={{ fontSize: '13px' }} />
          </button>
          <Popconfirm
            title="Delete Return"
            description="Are you sure you want to delete this return record?"
            onConfirm={() => handleDeleteCert(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <button
              style={{
                backgroundColor: '#dc2626',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                width: '26px',
                height: '26px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="Delete Return"
            >
              <DeleteOutlined style={{ fontSize: '13px' }} />
            </button>
          </Popconfirm>
        </div>
      )
    },
    {
      title: 'IEC / Company',
      dataIndex: 'iec_no',
      key: 'iec_no',
      width: 250,
      render: (text, record) => (
        <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
          {text || record.iec_no || activeIEC || '-'} {record.fullname || record.iec_name ? `- ${record.fullname || record.iec_name}` : ''}
        </span>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      render: (text) => <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{text || '-'}</span>
    },
    {
      title: 'Return Date',
      dataIndex: 'return_date',
      key: 'return_date',
      width: 130,
      render: (text) => <span style={{ color: '#374151', fontSize: '14px' }}>{text || '-'}</span>
    },
    {
      title: 'Tenure',
      dataIndex: 'tenure',
      key: 'tenure',
      width: 120,
      render: (text) => <span style={{ color: '#374151', fontSize: '14px' }}>{text || '-'}</span>
    },
    {
      title: 'Trigger Rate (Days)',
      dataIndex: 'trigger_rate',
      key: 'trigger_rate',
      width: 140,
      render: (text) => <span style={{ color: '#374151', fontSize: '14px' }}>{text || '-'}</span>
    },
    {
      title: 'Return Submitted',
      dataIndex: 'return_submitted',
      key: 'return_submitted',
      width: 150,
      render: (val) => (
        <Badge bg={val ? 'success' : 'secondary'}>
          {val ? 'Yes (Submitted)' : 'No'}
        </Badge>
      )
    },
    {
      title: 'Documents',
      dataIndex: 'file_path',
      key: 'file_path',
      width: 250,
      render: (text) => {
        const token = localStorage.getItem('token');
        const handleDownload = async (fileName) => {
          try {
            const response = await fetch(`${SERVER_URL}/download/${fileName}`, {
              method: 'GET',
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to download file');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          } catch (error) {
            console.error('Download failed:', error);
          }
        };

        const filesArray = Array.isArray(text) ? text : (typeof text === 'string' && text.length > 0 ? text.split(',') : []);

        return (
          <>
            {filesArray.length > 0 ? filesArray.map((fileName) => {
              const cleanName = cleanFilename(fileName.trim());
              return (
                <button
                  key={fileName}
                  onClick={() => handleDownload(fileName.trim())}
                  style={{
                    marginRight: '8px',
                    color: '#007bff',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {cleanName}
                </button>
              );
            }) : <span style={{ color: '#94a3b8' }}>-</span>}
          </>
        );
      }
    },
    {
      title: 'Approved',
      dataIndex: 'is_approved',
      key: 'is_approved',
      width: 100,
      render: (val, record) => {
        const isApproved = val === true || val === 'true' || val === 1 || val === '1';
        return (
          <Checkbox
            checked={isApproved}
            onChange={async (e) => {
              const checked = e.target.checked;
              const token = localStorage.getItem('token');
              const targetId = record.returns_id || record.std_certificate_id || record.id || record.key;

              setReturnData(prev => (prev || []).map(item => {
                const itemId = item.returns_id || item.std_certificate_id || item.id || item.key;
                if (itemId === targetId) {
                  return { ...item, is_approved: checked };
                }
                return item;
              }));

              setStdCertData(prev => ({
                ...prev,
                Returns: (prev.Returns || []).map(item => {
                  const itemId = item.returns_id || item.std_certificate_id || item.id || item.key;
                  if (itemId === targetId) {
                    return { ...item, is_approved: checked };
                  }
                  return item;
                })
              }));

              try {
                const formData = new FormData();
                formData.append('is_approved', checked);
                await updateReturns(targetId, formData, token);
                message.success('Approval status updated successfully');
                if (activeIEC) fetchDashboardData(activeIEC);
              } catch (error) {
                console.error(error);
                message.error('Error updating status');
              }
            }}
          />
        );
      }
    },
    {
      title: 'Email Sent At',
      dataIndex: 'email_sent_at',
      key: 'email_sent_at',
      width: 130,
      render: (text) => <span style={{ color: '#64748b', fontSize: '14px' }}>{text ? moment(text).format('DD-MM-YYYY') : '-'}</span>
    }
  ];

  const sampleFtaData = [
    {
      key: '1',
      filename: '20260506_112942_FTA Data Import - New Data 25.12.24== addition of NEPAL..xlsx',
      user_iec_no: '',
      fullname: '',
      status: 'Done',
      remarks: 'Success',
      created_at: '06-05-2026'
    },
    {
      key: '2',
      filename: '20241226_082626_FTA Data Import - New Data 25.12.24.xlsx',
      user_iec_no: '0388041871',
      fullname: 'ALFA LAVAL INDIA PRIVATE LIMITED - ALIPL',
      status: 'Done',
      remarks: 'Success',
      created_at: '26-12-2024'
    }
  ];

  const categorizedStatements = [
    {
      category: 'CERTIFICATES & COMPLIANCE',
      items: [
        { key: 'IEC', title: 'IEC', desc: 'Importer Exporter Code' },
        { key: 'EH', title: 'EH', desc: 'Export House Certificate' },
        { key: 'AEO', title: 'AEO', desc: 'Authorized Economic Operator Certificate' },
        { key: 'RCMC', title: 'RCMC', desc: 'Registration Cum Membership Certificate' },
        { key: 'FSP', title: 'FSP', desc: 'Foreign Supplier / Factory Status Certificate' },
        { key: 'DSC', title: 'DSC', desc: 'Digital Signature Certificate Expiry' },
        { key: 'Returns', title: 'Returns', desc: 'Compliance Returns' }
      ]
    }
  ];

  const dropdownMenuItems = categorizedStatements.flatMap((group, gIdx) => [
    {
      key: `header-${gIdx}`,
      type: 'group',
      label: (
        <div style={{ fontSize: '9px', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px', padding: '3px 8px 1px 8px', textTransform: 'uppercase' }}>
          {group.category}
        </div>
      ),
      children: group.items.map((item) => {
        const isSelected = selectedStatementType === item.key;
        return {
          key: item.key,
          onClick: () => {
            if (item.key === 'IEC') {
              handleShow(item.key);
            } else {
              setSelectedStatementType(item.key);
            }
          },
          label: (
            <div
              className="d-flex align-items-center justify-content-between"
              style={{
                padding: '4px 8px',
                borderRadius: '8px',
                backgroundColor: isSelected ? '#e6faf8' : 'transparent',
                borderLeft: isSelected ? '3px solid #25DBCD' : '3px solid transparent',
                transition: 'all 0.15s ease'
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#0d9488' : '#1e293b' }}>
                {item.title}
              </span>
              {isSelected && (
                <span style={{ color: '#25DBCD', fontWeight: 'bold', fontSize: '11px', marginLeft: '6px' }}>
                  ✓
                </span>
              )}
            </div>
          )
        };
      })
    }
  ]);

  const [loading, setLoading] = useState(false)

  // Compliance grouping and sub-tabs states
  const [allowedComplianceTabs, setAllowedComplianceTabs] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('Summary')
  const [selectedSubTab, setSelectedSubTab] = useState(() => {
    const fromUrl = searchParams.get('subtab');
    if (fromUrl) return fromUrl;
    const fromStorage = localStorage.getItem('selectedSubTab');
    if (fromStorage) return fromStorage;
    return 'Summary-Imp';
  });
  // Set true when a sub-tab switch is about to change `dates` (via the date-sync
  // effect). The summary-fetch effect below skips that intermediate render so it
  // doesn't fire once with the previous tab's range and again with the new one.
  const tabSwitchPendingRef = useRef(false);
  // Filters saved earlier in this browser-tab session (read once on mount).
  const savedFiltersRef = useRef(readDashFilters());
  const [summaryDashboardData, setSummaryDashboardData] = useState({})
  const [isLoading2, setIsLoading2] = useState(false)
  const summaryRequestRef = useRef(0)
  const [dutySavingError, setDutySavingError] = useState(null)
  const [selectedMode, setSelectedMode] = useState('All')
  const [selectedCurrency, setSelectedCurrency] = useState(() => savedFiltersRef.current.currency || 'INR')
  const [kpiMetricMode, setKpiMetricMode] = useState('count') // 'count' | 'amount'
  const [impDutyHscode, setImpDutyHscode] = useState('')
  const [impDutyAssessableValue, setImpDutyAssessableValue] = useState(100)
  const [impDutyHscodeOptions, setImpDutyHscodeOptions] = useState([])
  const [impDutyLoading, setImpDutyLoading] = useState(false)
  const fetchImpDutyHscodeOptions = useCallback(
    debounce(async (searchText) => {
      if (!searchText || searchText.length < 2) {
        setImpDutyHscodeOptions([])
        return
      }
      try {
        const results = await searchHscodeAutocomplete(searchText)
        setImpDutyHscodeOptions(results.map(item => ({ value: item, label: item })))
      } catch (error) {
        console.error('Autocomplete error:', error)
        setImpDutyHscodeOptions([])
      }
    }, 300),
    []
  )
  const [visibleColumns, setVisibleColumns] = useState(['description', 'count', 'amount', 'percentage'])

  // Group-head compare: IECs picked to view side by side, and each one's summary per card.
  const [compareIecs, setCompareIecs] = useState(() => {
    const saved = savedFiltersRef.current.compareIecs;
    return Array.isArray(saved) && saved.length >= 2 ? saved : [];
  })
  const [compareData, setCompareData] = useState({}) // { [cardName]: { [iec]: rawSummary | null } }
  const [compareLoading, setCompareLoading] = useState(false)
  const compareRequestRef = useRef(0)

  const categorySubTabs = {
    'Summary': [
      { key: 'Summary-Imp', label: 'Summary-Imp' },
      { key: 'Summary-Exp', label: 'Summary-Exp' },
      { key: 'EDPMS', label: 'EDPMS' }
    ],
    'Duty Credit and Refunds': [
      { key: 'DBK-AIR', label: 'DBK-All Industry Rate' },
      { key: 'IGST-Refund', label: 'IGST-Refund' },
      { key: 'RODTEP-Exp', label: 'RODTEP-Export' },
      { key: 'Rodtep-Util', label: 'RODTEP-Import Util.' }
    ],
    'Duty Exemption and Obligations': [
      { key: 'AdvAuth-EO', label: 'Adv. Auth -EO' },
      { key: 'AdvAuth-Utilization', label: 'Adv. Auth. Imp. Ledger' },
      { key: 'AdvAuthExcessImportLiab', label: 'Adv. Auth. Laib.' },
      { key: 'EPCG-Auth-EO', label: 'EPCG-Auth-EO' },
      { key: 'Bond-Utilization', label: 'Bond-Utilization' },
      // { key: 'Licenses-Detail', label: 'Adv. Auth. RDN Lic.' }
    ],
    'Smart Features': [
      { key: 'IMP-DUTY-RATE', label: 'IMP. Duty Rate' },
      { key: 'FTA-Opportunity', label: 'FTA Opportunities-Import' },
      { key: 'FTA-Applicability', label: 'FTA Applicability- Export' },
      { key: 'TAT-ImportClearance', label: 'Clearance TAT-Import' },
      { key: 'TAT-ExportClearance', label: 'Clearance TAT-Export' },
      { key: 'Inconsistency', label: 'Inconsistency Report' },
      { key: 'Duty-Saving-Report', label: 'Duty Saving Report' },
      { key: 'CCR', label: 'CCR' }
    ],
    'Audit': [
      { key: 'Executive-Consolidated-Audit', label: 'Executive Consolidated Audit' }
    ]
  }

  const subTabRoutes = {
    'AdvAuth-Utilization': '/dashboard/reports/advauth-import-report',
    'AdvAuthExcessImportLiab': '/dashboard/reports/advauth-excess-import-report',
    'CCR': '/dashboard/reports/ccr',
    'Bond-Utilization': '/dashboard/reports/bond-report',
    'Consolidated-Report-View': '/dashboard/consolidated-report'
  }

  // Scope & Dates
  const [IECOptions, setIECOptions] = useState([])
  const [activeIEC, setActiveIEC] = useState(() => {
    const role = localStorage.getItem('role');
    const iec = localStorage.getItem('iec_no');
    if (role === 'superadmin' || role === 'group_head') {
      let lastPicked = null;
      try { lastPicked = sessionStorage.getItem('selectedIEC'); } catch (e) { }
      return lastPicked || iec || 'ALL';
    }
    return iec || '';
  })
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name') || 'All Companies')
  // Keep the company name in step with the selected IEC (it may be restored from the session).
  useEffect(() => {
    if (!IECOptions.length || !activeIEC) return;
    const matched = IECOptions.find(opt => opt.iec_no === activeIEC);
    if (matched) setSelectedIECName(matched.fullname);
  }, [IECOptions, activeIEC]);

  const [cooCosFilter, setCooCosFilter] = useState(() => savedFiltersRef.current.cooCos || 'ALL');
  const [categoryRemarkFilter, setCategoryRemarkFilter] = useState(() => savedFiltersRef.current.categoryRemark || 'ALL');

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  // A date filter the user picked themselves (saved for the session) wins over the per-sub-tab defaults.
  const savedDate = savedFiltersRef.current.date;
  const userPickedDateRef = useRef(Boolean(savedDate));
  const [dateMode, setDateMode] = useState(savedDate?.mode || 'current_year');
  const [selectedFYYear, setSelectedFYYear] = useState(savedDate?.fy ? dayjs(`${savedDate.fy}-01-01`) : dayjs());
  const [selectedCYYear, setSelectedCYYear] = useState(savedDate?.cy ? dayjs(`${savedDate.cy}-01-01`) : dayjs());
  const [dates, setDates] = useState(() => {
    if (!savedDate) return [defaultFrom, defaultTo];
    return savedDate.from && savedDate.to ? [dayjs(savedDate.from), dayjs(savedDate.to)] : null;
  });

  useEffect(() => {
    if (!userPickedDateRef.current) return;
    saveDashFilters({
      date: {
        mode: dateMode,
        from: dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
        to: dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
        fy: selectedFYYear ? selectedFYYear.year() : null,
        cy: selectedCYYear ? selectedCYYear.year() : null
      }
    });
  }, [dateMode, dates, selectedFYYear, selectedCYYear]);

  useEffect(() => {
    saveDashFilters({ currency: selectedCurrency, cooCos: cooCosFilter, categoryRemark: categoryRemarkFilter, compareIecs });
  }, [selectedCurrency, cooCosFilter, categoryRemarkFilter, compareIecs]);

  // Sync date range defaults when switching sub-tabs:
  // Summary-Imp and Summary-Exp default to Past 5 Years; all others default to Current Year.
  // `dates` is a dependency of the data-fetching effects below, so we only replace it
  // (a new array reference) when the target range actually differs from the current one —
  // otherwise every sub-tab click was re-triggering unrelated fetches (cert data, FTA/CCR
  // status, license list/details, user charts) plus a duplicate TAT summary call.
  // `dates` is a dependency of the data-fetching effects below, so we only replace it
  // (a new array reference) when the target range actually differs from the current one —
  // otherwise every sub-tab click was re-triggering unrelated fetches (cert data, FTA/CCR
  // Sync date range defaults & category when switching sub-tabs:
  useEffect(() => {
    // 1. Sync category matching the subTab
    if (selectedSubTab) {
      for (const [catName, subTabList] of Object.entries(categorySubTabs)) {
        if (subTabList.some(item => item.key === selectedSubTab)) {
          setSelectedCategory(catName);
          break;
        }
      }
    }

    // 2. Sync date ranges — only while the user hasn't picked a date filter themselves
    if (userPickedDateRef.current) return;
    const isFiveYear = selectedSubTab === 'Summary-Imp' || selectedSubTab === 'Summary-Exp';
    const targetFrom = isFiveYear ? dayjs(`${currentYear - 4}-01-01`) : dayjs(`${currentYear}-01-01`);
    const targetTo = dayjs(`${currentYear}-12-31`);

    const newMode = isFiveYear ? '5_years' : 'current_year';
    setDateMode(newMode);
    localStorage.setItem('crd_dateMode', newMode);

    // Decide synchronously (from this render's `dates`) whether the switch will
    // change the range — the fetch effect reads this flag before the state update
    // lands, so it must be set here, not inside the updater below.
    const rangeWillChange = !(dates?.[0]?.isSame(targetFrom, 'day') && dates?.[1]?.isSame(targetTo, 'day'));
    if (rangeWillChange) {
      tabSwitchPendingRef.current = true;
    }

    setDates(prev => {
      if (prev?.[0]?.isSame(targetFrom, 'day') && prev?.[1]?.isSame(targetTo, 'day')) {
        return prev;
      }
      return [targetFrom, targetTo];
    });
  }, [selectedSubTab]);

  // Summary chart: download is triggered from the card header (via GraphView's imperative
  // handle) — so GraphView needs no in-plot control row.
  const summaryChartRef = useRef(null);

  const handleDateModeChange = (modeVal) => {
    userPickedDateRef.current = true;
    setDateMode(modeVal);
    localStorage.setItem('crd_dateMode', modeVal);
    if (modeVal === '5_years') {
      setDates([dayjs(`${currentYear - 4}-01-01`), dayjs(`${currentYear}-12-31`)]);
    } else if (modeVal === 'current_year') {
      setDates([dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)]);
    } else if (modeVal === 'last_year') {
      const ly = currentYear - 1;
      setDates([dayjs(`${ly}-01-01`), dayjs(`${ly}-12-31`)]);
    } else if (modeVal === 'till_date') {
      setDates(null);
    } else if (modeVal === 'fy') {
      const yr = selectedFYYear ? selectedFYYear.year() : currentYear;
      setDates([dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)]);
    } else if (modeVal === 'cy') {
      const yr = selectedCYYear ? selectedCYYear.year() : currentYear;
      setDates([dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)]);
    } else if (modeVal === 'custom') {
      if (!dates) {
        setDates([defaultFrom, defaultTo]);
      }
    }
  };

  const handleFYYearChange = (val) => {
    userPickedDateRef.current = true;
    setSelectedFYYear(val);
    if (val) {
      const yr = val.year();
      setDates([dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)]);
    } else {
      setDates(null);
    }
  };

  const handleCYYearChange = (val) => {
    userPickedDateRef.current = true;
    setSelectedCYYear(val);
    if (val) {
      const yr = val.year();
      setDates([dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)]);
    } else {
      setDates(null);
    }
  };

  const renderDateFilter = () => (
    <div className="d-flex align-items-center gap-2">
      <Select
        value={dateMode}
        onChange={handleDateModeChange}
        style={{ width: 150, borderRadius: '8px' }}
        size="middle"
        title="Duration"
      >
        <Select.Option value="5_years">Past 5 Years</Select.Option>
        <Select.Option value="current_year">Current Year</Select.Option>
        <Select.Option value="last_year">Last Year</Select.Option>
        <Select.Option value="till_date">Till Date</Select.Option>
        <Select.Option value="fy">Financial Year</Select.Option>
        <Select.Option value="cy">Calendar Year</Select.Option>
        <Select.Option value="custom">Custom</Select.Option>
      </Select>

      {dateMode === 'fy' && (
        <DatePicker
          picker="year"
          placeholder="Select FY"
          value={selectedFYYear}
          onChange={handleFYYearChange}
          size="middle"
          style={{ borderRadius: '8px', width: '130px' }}
          format={(val) => (val ? `FY ${val.year()}-${(val.year() + 1).toString().slice(-2)}` : '')}
        />
      )}

      {dateMode === 'cy' && (
        <DatePicker
          picker="year"
          placeholder="Select Year"
          value={selectedCYYear}
          onChange={handleCYYearChange}
          size="middle"
          style={{ borderRadius: '8px', width: '130px' }}
          format={(val) => (val ? `${val.year()}` : '')}
        />
      )}

      {dateMode === 'custom' && (
        <RangePicker
          value={dates}
          onChange={(val) => { userPickedDateRef.current = true; setDates(val || [defaultFrom, defaultTo]); }}
          style={{ borderRadius: '8px', width: '260px' }}
          size="middle"
        />
      )}
    </div>
  );

  // Expiry Calculations for IEC
  const today = moment();
  const nextMarch31 = today.isAfter(moment(`${currentYear}-03-31`))
    ? moment(`${currentYear + 1}-03-31`)
    : moment(`${currentYear}-03-31`);
  const daysLeft = nextMarch31.diff(today, 'days');
  const expiryDate = nextMarch31.format('MMMM Do, YYYY');

  // Modal and Consultation Form States
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [consultationForm, setConsultationForm] = useState({
    iec_name: userDetails?.name || "",
    name: "",
    contact_no: "",
    subject: "",
    message: "",
    email: "",
    iec: userDetails?.iec || ""
  });

  const handleClose = () => setShowModal(false);
  const handleShow = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleConsultChange = (e) => {
    const { name, value } = e.target;
    setConsultationForm(prev => ({ ...prev, [name]: value }));
  };

  const handleConsultSubmit = async (e) => {
    e.preventDefault();
    try {
      await sendConsultationRequest(consultationForm);
      message.success("Consultation request sent successfully!");
      setConsultationForm(prev => ({
        ...prev,
        name: "",
        contact_no: "",
        subject: "",
        message: "",
        email: "",
        iec_name: userDetails?.name || "",
        iec: userDetails?.iec || ""
      }));
      handleClose();
    } catch (err) {
      console.error(err);
      message.error("Failed to send request.");
    }
  };

  // Fetched Data Containers
  const [stdCertData, setStdCertData] = useState({})
  const [ftaFileList, setFtaFileList] = useState(sampleFtaData)
  const [ccrFileList, setCcrFileList] = useState([])
  const [returnData, setReturnData] = useState([])
  const [licenseOptions, setLicenseOptions] = useState([])

  // Edit Standard Certificate Modal State
  const [showEditCertModal, setShowEditCertModal] = useState(false);
  const [editingCertRecord, setEditingCertRecord] = useState(null);

  const handleEditCert = (record) => {
    setEditingCertRecord(record.rawItem || record);
    setShowEditCertModal(true);
  };

  const handleDeleteCert = async (record) => {
    try {
      if (selectedStatementType === 'Returns') {
        const id = record.returns_id || record.id || record.rawItem?.returns_id || record.rawItem?.id;
        if (!id) {
          message.error('Return ID missing.');
          return;
        }
        const token = localStorage.getItem('token');
        await deleteReturn(id, token);
        message.success('Return record deleted successfully!');
        if (activeIEC) {
          fetchDashboardData(activeIEC);
        }
        return;
      }
      const certId = record.std_certificate_id || record.id || record.rawItem?.std_certificate_id;
      if (!certId) {
        message.error('Certificate ID missing.');
        return;
      }
      const token = localStorage.getItem('token');
      await deleteStdCertificateDataService(certId, token);
      message.success('Standard certificate deleted successfully!');
      if (activeIEC) {
        fetchDashboardData(activeIEC);
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to delete record.');
    }
  };

  const handleCloseEditCertModal = () => {
    setShowEditCertModal(false);
    setEditingCertRecord(null);
    if (activeIEC) {
      fetchDashboardData(activeIEC);
    }
  };

  // Home Metrics
  const [homeMetrics, setHomeMetrics] = useState({
    importDutySaved: 0,
    exportRealization: 0,
    targetObligation: 0,
    fulfillmentActual: 0,
    complianceRate: 78
  })

  // Chart state definitions
  const [dutySavingChartSeries, setDutySavingChartSeries] = useState([
    { name: 'Import CIF Value (USD)', data: [0, 0, 0, 0, 0] },
    { name: 'Export FOB Value (USD)', data: [0, 0, 0, 0, 0] }
  ])
  const [dutySavingChartCategories, setDutySavingChartCategories] = useState(['2022', '2023', '2024', '2025', '2026'])

  const [categoryCounts, setCategoryCounts] = useState({
    A: 2, B: 1, C: 4, D: 0, E: 3, F: 2, G: 4
  })

  const [licExportData, setLicExportData] = useState([])
  const [licImportData, setLicImportData] = useState([])
  const [licFulfillmentSeries, setLicFulfillmentSeries] = useState([
    { name: 'Export Realised', data: [0, 0, 0, 0] },
    { name: 'Target obligation', data: [100, 100, 100, 100] }
  ])

  const [clearanceSeries, setClearanceSeries] = useState([
    { name: 'Import Clearance (Days)', data: [4.2, 1.8, 5.5] },
    { name: 'Export Clearance (Days)', data: [2.1, 0.9, 3.2] }
  ])

  const [brcPendingSeries, setBrcPendingSeries] = useState([
    { name: 'Pending Shipping Bills', data: [0, 0, 0, 0] }
  ])

  const [dutySavingAAEPCGSeries, setDutySavingAAEPCGSeries] = useState([65, 35])
  const [missedOpportunitySeries, setMissedOpportunitySeries] = useState([
    { name: 'Duty Saving Realised', data: [120, 150, 140, 180, 200, 220] },
    { name: 'Duty Missed (No FTA)', data: [15, 20, 10, 30, 25, 15] }
  ])

  // Color Palette
  const brandGold = '#25DBCD'        // was gold — now #25DBCD (UI accents/active states)
  const chartAccent = '#0EA5E9'      // sky — categorical partner to indigo in multi-series charts
  const brandSlate = '#8A92A6'
  const borderGrey = '#e9ecef'

  // Load list of IECs
  const loadIECList = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await getAllAdminIECsService(token)
      if (response && response.data) {
        setIECOptions(response.data)
        const role = userDetails.role;
        const userIec = localStorage.getItem('iec_no');
        if (role !== 'superadmin' && role !== 'group_head') {
          if (userIec) {
            setActiveIEC(userIec);
          } else if (response.data.length > 0) {
            setActiveIEC(response.data[0].iec_no);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load IEC list", error)
    }
  }

  // Fetch all primary dashboard metrics and categories
  const fetchDashboardData = async (iecNo) => {
    setLoading(true)
    const token = localStorage.getItem('token')
    const dateFilters = {
      from_date: dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null,
      to_date: dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null
    }

    try {
      // 1. Fetch Standard Certificates (Real data from DB)
      const certRes = await GetLatestStdCertificateDataService('', 1000, token, iecNo);
      if (certRes && certRes.data) {
        setStdCertData(certRes.data)
      } else {
        setStdCertData({})
      }

      // 1b. Fetch FTA & CCR Uploaded File Status Records (Real data from DB)
      try {
        const ftaUploadRes = await fileUploadStatusDataService('FTA', token);
        if (ftaUploadRes && ftaUploadRes.data && ftaUploadRes.data.length > 0) {
          setFtaFileList(ftaUploadRes.data.map((item, idx) => ({
            key: item.id || item.file_status_id || idx,
            filename: item.file_name || item.filename || '-',
            user_iec_no: item.iec_no || item.user_iec_no || iecNo || '-',
            fullname: item.iec_name || selectedIECName || '-',
            status: item.file_status || item.status || 'Done',
            remarks: item.remarks || item.remark || 'Success',
            created_at: item.created_at ? dayjs(item.created_at).format('DD-MM-YYYY') : '-'
          })));
        }
      } catch (err) {
        console.error('Error fetching FTA upload status:', err);
      }

      try {
        const ccrUploadRes = await fileUploadStatusDataService('CCR', token);
        if (ccrUploadRes && ccrUploadRes.data && ccrUploadRes.data.length > 0) {
          setCcrFileList(ccrUploadRes.data.map((item, idx) => ({
            key: item.id || item.file_status_id || idx,
            filename: item.file_name || item.filename || '-',
            user_iec_no: item.iec_no || item.user_iec_no || iecNo || '-',
            fullname: item.iec_name || selectedIECName || '-',
            status: item.file_status || item.status || 'Done',
            remarks: item.remarks || item.remark || 'Success',
            created_at: item.created_at ? dayjs(item.created_at).format('DD-MM-YYYY') : '-'
          })));
        }
      } catch (err) {
        console.error('Error fetching CCR upload status:', err);
      }

      // 2. Fetch Returns
      const returnsRes = await GetReturnsDataService(token, "all", (iecNo && iecNo !== 'ALL') ? iecNo : '');
      if (returnsRes && returnsRes.data) {
        setReturnData(returnsRes.data)
      } else {
        setReturnData([])
      }

      // 3. HOME-overview summaries — 8 report types in ONE call. This feeds ONLY the HOME tab's
      //    executive metrics (duty-saving trend, EO fulfilment, clearance TAT, BRC pending, …).
      //    The Summary tab loads its own single-report data separately, so we skip this heavy
      //    call unless the HOME tab is actually being shown — keeping first load fast.
      if (activeTab === 'HOME') {
        const reportTypes = [
          'Summary-Imp', 'Summary-Exp', 'EDPMS', 'AdvAuth-EO',
          'EPCG-Auth-EO', 'TAT-ImportClearance', 'TAT-ExportClearance', 'FTA-Opportunity'
        ]
        const summaryRes = await getDashboardReportSummaryData(reportTypes, dateFilters, iecNo)

        if (summaryRes && summaryRes.data) {
          const data = summaryRes.data

          // A. Summary-Imp & Summary-Exp
          const impSummary = data['Summary-Imp']?.dashboard_summary || []
          const expSummary = data['Summary-Exp']?.dashboard_summary || []

          const impTotalRow = impSummary.find(r => r.remark === 'Total') || {}
          const expTotalRow = expSummary.find(r => r.remark === 'Total') || {}

          // Year labels chronologically (last 5 years)
          const currentYear = dayjs().year()
          const last5Years = [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear].map(String)
          setDutySavingChartCategories(last5Years)

          const impTrendUSD = last5Years.map(year => {
            let sumYear = 0
            impSummary.forEach(r => {
              if (r.remark !== 'Total') {
                sumYear += r[`${year}_amount_usd`] || 0
              }
            })
            return Math.round(sumYear)
          })

          const expTrendUSD = last5Years.map(year => {
            let sumYear = 0
            expSummary.forEach(r => {
              if (r.remark !== 'Total') {
                sumYear += r[`${year}_amount_usd`] || 0
              }
            })
            return Math.round(sumYear)
          })

          setDutySavingChartSeries([
            { name: 'Import CIF Value (USD)', data: impTrendUSD },
            { name: 'Export FOB Value (USD)', data: expTrendUSD }
          ])

          // B. AdvAuth-EO & EPCG-Auth-EO
          const aaEOSummary = data['AdvAuth-EO'] || []
          const epcgEOSummary = data['EPCG-Auth-EO'] || []

          const aaTotalRow = aaEOSummary.find ? aaEOSummary.find(r => r.final_remark === 'Total') : (aaEOSummary[0] || {})
          const epcgTotalRow = epcgEOSummary.find ? epcgEOSummary.find(r => r.final_remark === 'Total') : (epcgEOSummary[0] || {})

          const aaTarget = aaTotalRow.total_fob_value_usd || 0
          const aaFulfilled = aaTotalRow.eo_fulfilled_val_usd || 0
          const epcgTarget = epcgTotalRow.total_fob_value_usd || 0
          const epcgFulfilled = epcgTotalRow.eo_fulfilled_val_usd || 0

          const totalTarget = aaTarget + epcgTarget
          const totalFulfilled = aaFulfilled + epcgFulfilled

          // C. EDPMS (Compliance Rate)
          const edpmsSummary = data['EDPMS']?.dashboard_summary || []
          const closedRow = edpmsSummary.find(r => r.description === 'Closed SHBs') || {}
          const complianceRateVal = closedRow.percentage != null ? closedRow.percentage : 78

          setHomeMetrics({
            importDutySaved: impTotalRow.amount_inr || 0,
            exportRealization: expTotalRow.amount_inr || 0,
            targetObligation: totalTarget,
            fulfillmentActual: totalFulfilled,
            complianceRate: complianceRateVal
          })

          // D. Category Counts
          const licCount = (aaEOSummary.length > 0 ? (aaEOSummary[0].count || 0) : 0) + (epcgEOSummary.length > 0 ? (epcgEOSummary[0].count || 0) : 0)
          setCategoryCounts(prev => ({
            ...prev,
            D: licCount || 0
          }))

          // E. TAT Clearance Times
          const tatImpSea = data['TAT-ImportClearance']?.Sea?.avg_clearance_days || 4.2
          const tatImpAir = data['TAT-ImportClearance']?.Air?.avg_clearance_days || 1.8
          const tatImpLand = data['TAT-ImportClearance']?.Land?.avg_clearance_days || 5.5

          const tatExpSea = data['TAT-ExportClearance']?.Sea?.avg_clearance_days || 2.1
          const tatExpAir = data['TAT-ExportClearance']?.Air?.avg_clearance_days || 0.9
          const tatExpLand = data['TAT-ExportClearance']?.Land?.avg_clearance_days || 3.2

          setClearanceSeries([
            { name: 'Import Clearance (Days)', data: [parseFloat(tatImpSea).toFixed(1), parseFloat(tatImpAir).toFixed(1), parseFloat(tatImpLand).toFixed(1)] },
            { name: 'Export Clearance (Days)', data: [parseFloat(tatExpSea).toFixed(1), parseFloat(tatExpAir).toFixed(1), parseFloat(tatExpLand).toFixed(1)] }
          ])

          // F. Unrealised BRC Count Grouped
          const pendingLess240 = edpmsSummary.find(r => r.description === 'Pending - less than 240 days')?.summary || 0
          const pending240to270 = edpmsSummary.find(r => r.description === 'Pending - 240 - to 270 days')?.summary || 0
          const pending270plus = edpmsSummary.find(r => r.description === 'Pending - 270+ days')?.summary || 0

          setBrcPendingSeries([
            { name: 'Pending Shipping Bills', data: [pendingLess240, pending240to270, pending270plus, 0] }
          ])

          // G. Duty Saving AA vs EPCG
          setDutySavingAAEPCGSeries([
            Math.round(aaTarget),
            Math.round(epcgTarget)
          ])

          // H. Missed Opportunities (FTA)
          const ftaSummary = data['FTA-Opportunity']?.dashboard_summary || []
          const eligibleTotal = ftaSummary.length > 0 ? (ftaSummary[0].total_eligible_usd || 0) : 100
          const availedTotal = ftaSummary.length > 0 ? (ftaSummary[0].bcd_availed_usd || 0) : 80
          const missedTotal = Math.max(0, eligibleTotal - availedTotal)

          setMissedOpportunitySeries([
            { name: 'Duty Saving Realised', data: [availedTotal * 0.15, availedTotal * 0.3, availedTotal * 0.5, availedTotal * 0.7, availedTotal * 0.85, availedTotal] },
            { name: 'Duty Missed (No FTA)', data: [missedTotal * 0.1, missedTotal * 0.25, missedTotal * 0.4, missedTotal * 0.6, missedTotal * 0.8, missedTotal] }
          ])
        }
      } // end HOME-only overview fetch
    } catch (error) {
      console.error("Error fetching dashboard data", error)
    } finally {
      setLoading(false)
    }
  }

  // Load Category D license numbers
  const fetchLicenseList = async (iecNo) => {
    if (!iecNo || iecNo === 'ALL') {
      setLicenseOptions([])
      setSelectedLicense(null)
      return
    }
    try {
      const data = await getReferenceDataService(iecNo, 'ADV_AUTH')
      if (data) {
        const options = data.map(item => item.license_number)
        setLicenseOptions(options)
        if (options.length > 0) {
          setSelectedLicense(options[0])
        } else {
          setSelectedLicense(null)
        }
      }
    } catch (error) {
      console.error("Failed to load licenses list", error)
    }
  }

  // Fetch individual license tracking tables
  const fetchLicenseDetails = async (iecNo, licenseNo) => {
    if (!iecNo || !licenseNo || iecNo === 'ALL') {
      setLicExportData([])
      setLicImportData([])
      return
    }
    try {
      const res = await getAdvAuthRdnLicReportService(iecNo, { bill_no: licenseNo })
      if (res) {
        setLicExportData(res.export_st || [])
        setLicImportData(res.import_st || [])

        // Target vs realized
        const totalObligation = res.master_summary?.[0]?.total_fob_value_usd || 100
        const realizedExport = res.master_summary?.[0]?.eo_fulfilled_val_usd || 0

        setLicFulfillmentSeries([
          { name: 'Export Realised', data: [0, Math.round(realizedExport * 0.4), Math.round(realizedExport * 0.7), Math.round(realizedExport)] },
          { name: 'Target obligation', data: [totalObligation, totalObligation, totalObligation, totalObligation] }
        ])
      }
    } catch (error) {
      console.error("Failed to load license details", error)
    }
  }

  // Handle IEC change
  const handleIECChange = (val) => {
    setActiveIEC(val)
    try {
      sessionStorage.setItem("selectedIEC", val)
    } catch (e) { }
    const matched = IECOptions.find(opt => opt.iec_no === val)
    setSelectedIECName(matched ? matched.fullname : 'All Companies')
  }

  // Trigger loading on Mount / IEC Change / Dates Change
  useEffect(() => {
    loadIECList()
  }, [])

  useEffect(() => {
    const fetchComplianceRbac = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await userRbac(token);
        const items = response?.data?.data || [];
        const complianceItems = items.filter(i => i.page_type === 'COMPLIANCE');
        if (complianceItems.length === 0) {
          setAllowedComplianceTabs(null);
        } else {
          const allowedSet = new Set(complianceItems.map(i => i.page_desc));
          setAllowedComplianceTabs(allowedSet);

          // If the default selected sub-tab is not allowed, switch to the first allowed one
          if (!allowedSet.has('Summary-Imp')) {
            let firstAllowed = null;
            for (const cat of Object.keys(categorySubTabs)) {
              const matched = categorySubTabs[cat].find(t => allowedSet.has(t.key));
              if (matched) {
                firstAllowed = matched.key;
                break;
              }
            }
            if (firstAllowed) {
              setSelectedSubTab(firstAllowed);
            }
          }
        }
      } catch (e) {
        console.error('fetchComplianceRbac error:', e);
        setAllowedComplianceTabs(null);
      }
    };

    if (userDetails.role === 'superadmin' || userDetails.role === 'group_head') {
      setAllowedComplianceTabs(null);
    } else {
      fetchComplianceRbac();
    }
  }, [userDetails.role]);

  const prevDashIecRef = useRef(null)
  useEffect(() => {
    // Always drop cached per-tab summaries so no stale data from the previous
    // date range is shown when the filter changes.
    setSummaryDashboardData({})

    // The certificate / upload-status / returns / license-list / charts fetches
    // below are IEC-scoped and date-independent — only re-run them when the IEC
    // actually changes (not on every sub-tab-driven date-range change).
    const iecChanged = prevDashIecRef.current !== activeIEC
    prevDashIecRef.current = activeIEC

    if (iecChanged) {
      try {
        sessionStorage.setItem("selectedIEC", activeIEC)
      } catch (e) { }
      fetchLicenseList(activeIEC)
      fetchUserCharts()
    }

    // fetchDashboardData also builds the HOME-tab date-scoped overview, so run it
    // on IEC change (always) and on date change only while the HOME tab is shown.
    if (iecChanged || activeTab === 'HOME') {
      fetchDashboardData(activeIEC)
    }
  }, [activeIEC, dates, activeTab, fetchUserCharts])

  useEffect(() => {
    if (selectedLicense) {
      fetchLicenseDetails(activeIEC, selectedLicense)
    }
  }, [selectedLicense, activeIEC])

  const getSummaryDashboard = async (complianceType, dateRange = null, cooCosOverride = undefined, categoryRemarkOverride = undefined) => {
    const range = dateRange || dates;
    const currentCooCos = cooCosOverride !== undefined ? cooCosOverride : cooCosFilter;
    const currentCategoryRemark = categoryRemarkOverride !== undefined ? categoryRemarkOverride : categoryRemarkFilter;
    const date_filters = {
      from_date: range?.[0] ? dayjs(range[0]).format("YYYY-MM-DD") : null,
      to_date: range?.[1] ? dayjs(range[1]).format("YYYY-MM-DD") : null,
      coo_cos: currentCooCos !== 'ALL' ? currentCooCos : null,
      category_remark: currentCategoryRemark !== 'ALL' ? currentCategoryRemark : null
    };

    // These sub-tabs render self-contained views and have no bulk summary endpoint.
    const requestId = ++summaryRequestRef.current;
    if (['Licenses-Detail', 'IMP-DUTY-RATE', 'Executive-Consolidated-Audit'].includes(complianceType)) {
      setIsLoading2(false);
      return;
    }

    // Aggregate raw records into { headers, dashboard_summary } for AdvAuthExcessImportLiab
    const buildExcessImportSummary = (records) => {
      if (!records || records.length === 0) return { headers: [], dashboard_summary: [] };

      // Group by auth_number, check if any item has excess imports (actual > auth qty)
      const authMap = {};
      records.forEach(r => {
        const auth = r.auth_number;
        if (!authMap[auth]) authMap[auth] = { excess: false, count: 0 };
        authMap[auth].count += 1;
        const actual = parseFloat(r.actual_imports_made) || 0;
        const authQty = parseFloat(r.imp_qty_per_auth) || 0;
        if (actual > authQty) authMap[auth].excess = true;
      });

      const totalAuths = Object.keys(authMap).length;
      const excessAuths = Object.values(authMap).filter(a => a.excess).length;
      const withinLimitAuths = totalAuths - excessAuths;

      const summary = [
        { description: 'Total Authorizations', summary: totalAuths, percentage: 100, color: 'black' },
        { description: 'Within Import Limit', summary: withinLimitAuths, percentage: totalAuths ? Math.round((withinLimitAuths / totalAuths) * 100) : 0, color: 'green' },
        { description: 'Excess Import Liability', summary: excessAuths, percentage: totalAuths ? Math.round((excessAuths / totalAuths) * 100) : 0, color: 'red' },
      ];

      return {
        headers: [
          { title: 'Description', dataIndex: 'description', key: 'description' },
          { title: 'Auth. Count', dataIndex: 'summary', key: 'summary', align: 'right' },
          { title: 'Percentage (%)', dataIndex: 'percentage', key: 'percentage', align: 'right' },
        ],
        dashboard_summary: summary,
      };
    };

    // Aggregate raw records into { headers, dashboard_summary } for AdvAuth-Utilization
    const buildUtilizationSummary = (records) => {
      if (!records || records.length === 0) return { headers: [], dashboard_summary: [] };

      const totalAuths = records.length;
      let fullyUtilized = 0, partiallyUtilized = 0, notUtilized = 0;
      let totalAuthQty = 0, totalUsedQty = 0;

      records.forEach(r => {
        const authQty = parseFloat(r.auth_qty || r.quantity) || 0;
        const usedQty = parseFloat(r.total_qty) || 0;
        totalAuthQty += authQty;
        totalUsedQty += usedQty;
        const utilPct = authQty > 0 ? (usedQty / authQty) * 100 : 0;
        if (utilPct >= 100) fullyUtilized++;
        else if (utilPct > 0) partiallyUtilized++;
        else notUtilized++;
      });

      const summary = [
        { description: 'Total Authorizations', summary: totalAuths, percentage: 100, color: 'black' },
        { description: 'Fully Utilized (≥100%)', summary: fullyUtilized, percentage: totalAuths ? Math.round((fullyUtilized / totalAuths) * 100) : 0, color: 'green' },
        { description: 'Partially Utilized', summary: partiallyUtilized, percentage: totalAuths ? Math.round((partiallyUtilized / totalAuths) * 100) : 0, color: 'orange' },
        { description: 'Not Utilized', summary: notUtilized, percentage: totalAuths ? Math.round((notUtilized / totalAuths) * 100) : 0, color: 'red' },
      ];

      return {
        headers: [
          { title: 'Description', dataIndex: 'description', key: 'description' },
          { title: 'Auth. Count', dataIndex: 'summary', key: 'summary', align: 'right' },
          { title: 'Percentage (%)', dataIndex: 'percentage', key: 'percentage', align: 'right' },
        ],
        dashboard_summary: summary,
      };
    };

    // These tabs use the single GET /get-report-summary endpoint,
    // not the bulk POST /get-dashboard-report-summary
    const singleEndpointMap = {
      'AdvAuthExcessImportLiab': 'AdvLic-ExcessImp',
      'AdvAuth-Utilization': 'AdvAuth-ScripLedger',
    };

    try {
      setIsLoading2(true);
      if (complianceType === 'Duty-Saving-Report') setDutySavingError(null);

      if (singleEndpointMap[complianceType]) {
        const backendKey = singleEndpointMap[complianceType];
        const response = await getReportSummaryData(backendKey, date_filters, activeIEC);
        if (requestId !== summaryRequestRef.current) return;
        const rawData = response?.data;
        let normalized;
        if (Array.isArray(rawData)) {
          if (complianceType === 'AdvAuthExcessImportLiab') {
            normalized = buildExcessImportSummary(rawData);
          } else if (complianceType === 'AdvAuth-Utilization') {
            normalized = buildUtilizationSummary(rawData);
          } else {
            normalized = { headers: [], dashboard_summary: rawData };
          }
        } else {
          normalized = rawData;
        }
        setSummaryDashboardData(prevData => ({
          ...prevData,
          [complianceType]: normalized
        }));
      } else {
        const response = await getDashboardReportSummaryData(complianceType, date_filters, activeIEC);
        if (requestId !== summaryRequestRef.current) return;
        if (complianceType === 'Duty-Saving-Report' && (
          !Array.isArray(response?.data?.[complianceType]?.dashboard_summary) ||
          !Array.isArray(response?.data?.[complianceType]?.trade_benefits_millions) ||
          !Array.isArray(response?.data?.[complianceType]?.inr?.dashboard_summary) ||
          !Array.isArray(response?.data?.[complianceType]?.inr?.trade_benefits_millions)
        )) {
          throw new Error('Invalid duty saving summary response');
        }
        if (response && response.data) {
          setSummaryDashboardData(prevData => ({
            ...prevData,
            ...response.data
          }));
        }
      }

    } catch (err) {
      if (requestId !== summaryRequestRef.current) return;
      if (complianceType === 'Duty-Saving-Report') {
        setDutySavingError('Unable to load the Duty Saving Report. Please try again.');
      }
      console.error(err);
    } finally {
      if (requestId === summaryRequestRef.current) setIsLoading2(false);
    }
  }

  // ── Group-head compare (several IECs side by side) ──────────────────────────
  // Group heads only for now — superadmins keep the single-company picker.
  const canCompareIecs = userDetails.role === 'group_head';
  // Sub-tabs with their own layouts or a different summary endpoint have no compare view.
  const COMPARE_UNSUPPORTED = [
    'Licenses-Detail', 'IMP-DUTY-RATE', 'Executive-Consolidated-Audit', 'Duty-Saving-Report',
    'Inconsistency', 'CCR', 'AdvAuthExcessImportLiab', 'AdvAuth-Utilization'
  ];
  const isComparing = canCompareIecs && activeTab === 'CIP' && compareIecs.length >= 2;
  const isCompareActive = isComparing && !COMPARE_UNSUPPORTED.includes(selectedSubTab);

  // The compliance tab's "Select Company" is a multi-select: it shows the compare picks,
  // or the single active company, or nothing (All Companies).
  const selectedCompanyIecs = compareIecs.length >= 2
    ? compareIecs
    : (activeIEC && activeIEC !== 'ALL' ? [activeIEC] : []);

  const handleCompanySelection = (iecs) => {
    if (iecs.length >= 2) {
      setCompareIecs(iecs);
      return;
    }
    setCompareIecs([]);
    const next = iecs[0] || 'ALL';
    if (next !== activeIEC) handleIECChange(next);
  };

  const fetchCompareData = async (cardName) => {
    const range = dates;
    const date_filters = {
      from_date: range?.[0] ? dayjs(range[0]).format("YYYY-MM-DD") : null,
      to_date: range?.[1] ? dayjs(range[1]).format("YYYY-MM-DD") : null,
      coo_cos: cooCosFilter !== 'ALL' ? cooCosFilter : null,
      category_remark: categoryRemarkFilter !== 'ALL' ? categoryRemarkFilter : null
    };
    const requestId = ++compareRequestRef.current;
    setCompareLoading(true);
    setCompareData(prev => ({ ...prev, [cardName]: {} }));

    const results = await Promise.all(compareIecs.map(async (iec) => {
      try {
        const response = await getDashboardReportSummaryData(cardName, date_filters, iec);
        return [iec, response?.data?.[cardName] || null];
      } catch (err) {
        console.error(`Compare: failed to load ${cardName} for ${iec}`, err);
        return [iec, null];
      }
    }));

    if (requestId !== compareRequestRef.current) return;
    setCompareData(prev => ({ ...prev, [cardName]: Object.fromEntries(results) }));
    setCompareLoading(false);
  }

  // ── Resolve the summary rows for a card, transparently handling multi-mode ──
  const getSummaryRows = (cardName) => {
    const rawData = summaryDashboardData[cardName];
    if (!rawData) return null;
    if (rawData.dashboard_summary) return rawData.dashboard_summary;
    // multi-mode (All / Sea / Air / Land)
    const modes = ['All', 'Sea', 'Air', 'Land'].filter(m => rawData[m]);
    if (modes.length) {
      const current = modes.includes(selectedMode) ? selectedMode : modes[0];
      return rawData[current]?.dashboard_summary || null;
    }
    return null;
  };

  // A row is "count-based" (suitable for KPI tiles) when it exposes a numeric summary count.
  const isCountRow = (r) => r && r.summary !== undefined && r.summary !== null && !Number.isNaN(Number(r.summary));
  const clampPct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

  // ── KPI stat-tile band (real data from the summary rows) ────────────────────
  // `rowsOverride` / `extraMetaFor` let the compare view reuse the band with combined rows
  // and a per-tile note naming the outlier IEC.
  const renderKpiBand = (cardName, rowsOverride = null, extraMetaFor = null) => {
    const rows = rowsOverride || getSummaryRows(cardName);
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const countRows = rows.filter(isCountRow);
    if (countRows.length < 2) return null; // not a count-style summary — leave untouched

    const tiles = countRows.slice(0, 5); // keep to a single row on desktop
    const isAmountMode = kpiMetricMode === 'amount';
    const currencyLabel = selectedCurrency === 'USD' ? 'USD' : 'Rs.';

    return (
      <div className="cip-band">
        {tiles.map((row, idx) => {
          let baseLabel = row.description || row.remark || row.business_unit || 'Metric';
          baseLabel = baseLabel.replace(/\s*\((Rs\.|INR|USD)\)/gi, '').trim();
          // Prefer the backend's explicit row colour so tiles match the remark colours exactly.
          const key = statusKeyFromRow(row);
          const hasPct = row.percentage !== undefined && row.percentage !== null && !Number.isNaN(Number(row.percentage));
          const pct = hasPct ? clampPct(row.percentage) : null;

          const displayCount = formatCount(row.summary);

          // Resolve the row's monetary value from the backend fields. USD is shown strictly as
          // the backend provides it (converted per exact-date exchange rate) — there is no
          // INR→USD fabrication, so the tile always agrees with the report table (which shows
          // $0.00 where a USD rate was unavailable). A row carrying any INR/USD amount is
          // "monetary" and always renders a currency value (0 where that currency is missing);
          // only rows with no amount at all fall back to showing the count as the main value.
          const amountInr = row.amount_inr ?? row.amount ?? row.valueInINR ?? row.val ?? row.value ?? null;
          const amountUsd = row.amount_usd ?? row.usd_amount ?? null;
          const isMonetaryRow = (amountInr !== null && amountInr !== undefined)
            || (amountUsd !== null && amountUsd !== undefined);
          const currencyCode = selectedCurrency === 'USD' ? 'USD' : 'INR';
          const rawAmount = isMonetaryRow
            ? (selectedCurrency === 'USD' ? (amountUsd ?? 0) : (amountInr ?? 0))
            : null;
          const hasAmount = rawAmount !== null && rawAmount !== undefined;
          const formattedAmount = hasAmount ? formatCurrencyCompact(rawAmount, currencyCode) : null;

          return (
            <div
              key={idx}
              className={`cip-tile cip-tile--${key}`}
              style={{ transition: 'all 0.15s ease' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', width: '100%' }}>
                <div className="cip-tile__label" title={baseLabel} style={{ margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {baseLabel}
                </div>
                {/* When the amount is the main value, surface the count here (number only, no label). */}
                {hasAmount && (
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#334155', backgroundColor: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                    {displayCount}
                  </div>
                )}
              </div>

              <div
                className="cip-tile__value"
                title={hasAmount ? formatCurrency(rawAmount, currencyCode) : undefined}
                style={{ fontSize: '17px', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginTop: '6px', fontFamily: hasAmount ? 'monospace' : 'inherit' }}
              >
                {hasAmount ? formattedAmount : displayCount}
              </div>

              {hasPct && (
                <div className="cip-tile__meta" style={{ color: key === 'primary' ? '#64748B' : (STATUS[key]?.fg || '#64748B'), marginTop: '4px', fontWeight: 600 }}>
                  {pct}% of total
                </div>
              )}
              {extraMetaFor && extraMetaFor(row) && (
                <div className="cip-tile__meta" style={{ color: '#64748B', fontWeight: 500 }}>
                  {extraMetaFor(row)}
                </div>
              )}
              {hasPct && (
                <div className="cip-tile__meter">
                  <div className="cip-tile__fill" style={{ width: `${pct}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDistributionStrip = (cardName) => {
    const rows = getSummaryRows(cardName);
    if (!Array.isArray(rows) || rows.length === 0) return null;

    // breakdown rows = count rows that carry a percentage and are not the grand total
    const parts = rows
      .filter(isCountRow)
      .filter(r => {
        const rawLabel = r.description || r.remark || r.final_remark || r.business_unit || r.particulars || r.category || '';
        const d = String(rawLabel).toLowerCase();
        const hasPct = r.percentage !== undefined && r.percentage !== null && !Number.isNaN(Number(r.percentage));
        return hasPct && !/total|grand/.test(d);
      })
      .map(r => {
        let label = r.description || r.remark || r.final_remark || r.business_unit || r.particulars || r.category || 'Metric';
        label = label.replace(/\s*\((Rs\.|INR|USD)\)/gi, '').trim();
        // Prefer the backend's explicit row colour so segments match the remark colours exactly.
        const key = statusKeyFromRow(r);
        return {
          label,
          pct: clampPct(r.percentage),
          count: Number(r.summary) || 0,
          key,
          color: STATUS[key]?.fg || '#25DBCD'
        };
      })
      .filter(p => p.pct > 0);

    if (parts.length === 0) return null;

    const totalRow = rows.find(r => /total|grand/i.test(String(r.description || r.remark || r.particulars || '')));
    const totalCount = totalRow ? formatCount(totalRow.summary) : formatCount(parts.reduce((s, p) => s + p.count, 0));

    return (
      <div className="cip-dist">
        <div className="cip-dist__head">
          <span className="cip-dist__title">Distribution breakdown</span>
          <span className="cip-dist__total">Total: <b>{totalCount}</b></span>
        </div>
        <div className="cip-dist__bar">
          {parts.map((p, i) => (
            <div
              key={i}
              className={`cip-dist__seg cip-dist__seg--${p.key}`}
              style={{ width: `${p.pct}%`, backgroundColor: p.color }}
              title={`${p.label} — ${p.pct}%`}
            />
          ))}
        </div>
        <div className="cip-dist__legend" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: '8px' }}>
          {parts.map((p, i) => (
            <span key={i} className="cip-dist__legend-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
              <span className="sq" style={{ width: '10px', height: '10px', borderRadius: '50%', background: p.color, display: 'inline-block' }} />
              <span style={{ color: '#475569', fontWeight: 500 }}>{p.label}:</span>
              <b style={{ color: '#0f172a' }}>{p.pct}%</b>
              <span style={{ color: '#94a3b8', fontSize: '11.5px' }}>({formatCount(p.count)})</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  // ── Compare view: combined KPI band + side-by-side table for the picked IECs ──
  const renderCompareView = (cardName) => {
    const perIec = compareData[cardName] || {};
    const modes = availableModes(compareIecs.map(iec => perIec[iec]));
    const currentMode = modes.includes(selectedMode) ? selectedMode : modes[0];
    // Companies with nothing to report for this card go last, greyed out.
    const entries = compareIecs
      .map(iec => {
        const opt = IECOptions.find(o => o.iec_no === iec);
        const raw = perIec[iec];
        const block = resolveSummaryBlock(raw, currentMode);
        return {
          iec,
          fullname: opt?.fullname || iec,
          short: shortIecName(opt?.fullname, iec),
          raw,
          block,
          empty: raw !== undefined && !hasSummaryData(block?.dashboard_summary)
        };
      })
      .sort((a, b) => Number(a.empty) - Number(b.empty));
    const iecs = entries.map(({ iec, fullname, short, empty }) => ({ iec, fullname, short, empty }));
    const blocks = entries.map(e => e.block);
    const raws = entries.map(e => e.raw);
    const headerBlock = blocks.find(b => Array.isArray(b?.headers) && b.headers.length);

    if (!headerBlock) {
      return compareLoading ? (
        <div className="d-flex justify-content-center p-4"><Spin size="large" tip="Loading companies..." /></div>
      ) : (
        <div className="text-muted text-center p-4">No data available for these companies.</div>
      );
    }

    const { labels, combined, byLabel } = combineSummaryRows(blocks.map(b => b?.dashboard_summary));
    const failed = iecs.filter((_, k) => raws[k] === null);
    const subTabLabel = Object.values(categorySubTabs).flat().find(t => t.key === cardName)?.label || cardName;

    // Tile note: the IEC furthest from target for that row.
    const tileMeta = (row) => {
      const label = rowLabel(row);
      const key = statusKeyFromRow(row);
      if (key === 'primary') {
        if (!/total/i.test(label)) return null;
        const withData = iecs.filter(i => !i.empty).length;
        return withData < iecs.length ? `${withData} of ${iecs.length} IECs have data` : `Across ${iecs.length} IECs`;
      }
      const shares = iecs
        .map((iec, k) => ({ name: iec.short, empty: iec.empty, v: toNum(byLabel[k].get(label)?.percentage) }))
        .filter(s => !s.empty && s.v !== null);
      if (shares.length < 2) return null;
      const pick = shares.reduce((a, b) => (key === 'success' ? (b.v < a.v ? b : a) : (b.v > a.v ? b : a)));
      return `${key === 'success' ? 'Lowest' : 'Highest'}: ${pick.name} ${pick.v}%`;
    };

    return (
      <div>
        {renderKpiBand(cardName, combined, tileMeta)}
        <div style={{ border: `1px solid ${borderGrey}`, borderRadius: '8px', overflow: 'hidden' }}>
          <div
            className="d-flex align-items-center justify-content-between px-3"
            style={{ backgroundColor: '#f8fafc', borderBottom: `1px solid ${borderGrey}`, minHeight: '52px' }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="fa-solid fa-table-cells text-primary" style={{ fontSize: '14px' }}></i>
              <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>{subTabLabel} by company</span>
              <span style={{ fontSize: '12px', color: '#64748b' }}>{iecs.length} IECs</span>
            </div>
            {modes.length > 0 && (
              <Radio.Group value={currentMode} onChange={(e) => setSelectedMode(e.target.value)} buttonStyle="solid" size="small">
                {modes.map(mode => <Radio.Button key={mode} value={mode}>{mode}</Radio.Button>)}
              </Radio.Group>
            )}
          </div>
          <div style={{ padding: '8px' }}>
            {failed.length > 0 && (
              <div className="cip-compare-hint">
                Couldn't load {failed.map(f => f.short).join(', ')}. Their columns are left blank.
              </div>
            )}
            <CompareIecTable
              cardName={cardName}
              iecs={iecs}
              headers={leafHeaders(headerBlock.headers)}
              labels={labels}
              combined={combined}
              byLabel={byLabel}
              currency={selectedCurrency}
              visibleColumns={visibleColumns}
              loading={compareLoading}
              formatAmountCompact={formatCurrencyCompact}
              formatAmountFull={formatCurrency}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleDownloadReport = async (cardName, categoryText = 'ALL', typeLabel = 'Summary') => {
    const iec = activeIEC || localStorage.getItem('iec_no') || 'ALL';
    const fromDate = dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
    const toDate = dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;
    try {
      message.loading({ content: `Downloading ${cardName} ${typeLabel} Report...`, key: 'rpt_dl', duration: 0 });
      const response = await downloadDashboardReportService(cardName, iec, categoryText, fromDate, toDate);
      if (response && response.data) {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeReport = String(cardName).replace(/ /g, '_');
        const safeCategory = String(typeLabel).replace(/ /g, '_');
        link.setAttribute('download', `${safeReport}_${safeCategory}_${iec}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success({ content: `${typeLabel} Report downloaded successfully!`, key: 'rpt_dl' });
      } else {
        message.error({ content: 'Download failed. Please try again.', key: 'rpt_dl' });
      }
    } catch (err) {
      console.error('Report download error:', err);
      message.error({ content: 'Download failed. Please try again.', key: 'rpt_dl' });
    }
  };

  const handleDownloadPendencySummary = async (cardNameOrFileType) => {
    let reportType = cardNameOrFileType;
    if (cardNameOrFileType === 'BOE' || cardNameOrFileType === 'Import') reportType = 'Summary-Imp';
    if (cardNameOrFileType === 'SHB' || cardNameOrFileType === 'Export') reportType = 'Summary-Exp';

    const isImport = reportType === 'Summary-Imp';
    const isExport = reportType === 'Summary-Exp';
    const reportTitle = isImport ? 'Import Summary' : isExport ? 'Export Summary' : `${reportType} Summary`;
    const iec = activeIEC || localStorage.getItem('iec_no') || 'ALL';
    const fromDate = dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
    const toDate = dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;
    try {
      message.loading({ content: `Downloading ${reportTitle} Report...`, key: 'pendency_dl', duration: 0 });
      let response;
      try {
        if (isImport || isExport) {
          const fileType = isImport ? 'BOE' : 'SHB';
          const token = localStorage.getItem('token');
          response = await downloadPendencySummary(token, fileType, undefined, "CIP Compliance", true, fromDate, toDate, iec !== 'ALL' ? iec : null);
        } else {
          response = await downloadDashboardReportService(reportType, iec, 'ALL', fromDate, toDate);
        }
      } catch (e1) {
        console.warn('Backend download fallback to downloadDashboardReportService:', e1);
        response = await downloadDashboardReportService(reportType, iec, 'ALL', fromDate, toDate);
      }
      if (response && response.data) {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        const safeTitle = reportTitle.replace(/[^a-zA-Z0-9]/g, '_');
        link.setAttribute('download', `${safeTitle}_${timestamp}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success({ content: `${reportTitle} Report downloaded successfully!`, key: 'pendency_dl' });
      } else {
        message.error({ content: 'Download failed. Please try again.', key: 'pendency_dl' });
      }
    } catch (error) {
      console.error('Download failed:', error);
      message.error({ content: 'Failed to download report. Please try again.', key: 'pendency_dl' });
    }
  };

  // ── Exact 1:1 Excel Summary Sheet Renderer (matching official exported Excel report) ──
  const [excelSheetTab, setExcelSheetTab] = useState("Summary Report");
  const [previewApiRows, setPreviewApiRows] = useState([]);
  const [previewStandardsRows, setPreviewStandardsRows] = useState([]);
  const [previewUserEmail, setPreviewUserEmail] = useState("");
  const [loadingPreviewData, setLoadingPreviewData] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const doFetchPreviewData = async () => {
    setLoadingPreviewData(true);
    setPreviewError(null);
    setPreviewApiRows([]);
    setPreviewStandardsRows([]);
    setPreviewUserEmail("");
    try {
      const token = localStorage.getItem('token');
      const fromDateStr = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const toDateStr = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;
      const res = await getConsolidatedReportPreviewDataService(token, activeIEC, fromDateStr, toDateStr);
      if (res && res.sheet1 && Array.isArray(res.sheet1) && res.sheet1.length > 0) {
        setPreviewApiRows(res.sheet1);
      } else {
        setPreviewError('Summary report data not available for this IEC.');
      }
      if (res && res.sheet2 && Array.isArray(res.sheet2) && res.sheet2.length > 0) {
        setPreviewStandardsRows(res.sheet2);
      }
      if (res && res.user_email) {
        setPreviewUserEmail(res.user_email);
      }
    } catch (err) {
      console.error("Error fetching preview data:", err);
      setPreviewError('Failed to load report data. Please try again.');
    } finally {
      setLoadingPreviewData(false);
    }
  };

  useEffect(() => {
    // The Executive-Consolidated-Audit sub-tab renders <ConsolidatedReportView>,
    // which fetches its own preview data — only fetch here for the legacy modal.
    if (showConsolidatedModal) {
      doFetchPreviewData();
    }
  }, [showConsolidatedModal, activeIEC, dates]);

  const renderExactExcelSummarySheet = () => {
    const timeStr = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const userEmail = previewUserEmail || (activeIEC === '0388081627' ? 'cumminsin@lucrative.co.in' : (userDetails?.email || localStorage.getItem('email') || localStorage.getItem('user_email') || localStorage.getItem('username') || 'admin@company.com'));
    const currLabel = selectedCurrency === 'USD' ? 'USD' : 'INR';

    // Sheet 1 rows: always from backend (same as Excel)
    const sheet1Rows = previewApiRows;

    // Sheet 2 rows: always from backend (same as Excel)
    const standardsRows = previewStandardsRows;

    // Error / empty state
    const showError = !loadingPreviewData && previewError;
    const showEmptySheet1 = !loadingPreviewData && !previewError && sheet1Rows.length === 0;
    const showEmptySheet2 = !loadingPreviewData && !previewError && standardsRows.length === 0;

    return (
      <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', overflow: 'hidden', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
        {/* Modern UI Header Banner */}
        <div style={{ backgroundColor: '#0f172a', color: '#ffffff', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '15px', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🏢</span>
              <span>{selectedIECName || localStorage.getItem('name') || 'Cummins Generator Technologies India Private Limited'}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px', fontWeight: 500 }}>
              IEC: {activeIEC || localStorage.getItem('iec_no') || '0388081627'} • Currency: {currLabel}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '6px', background: '#1e293b', padding: '3px', borderRadius: '6px' }}>
              <button
                type="button"
                onClick={() => setExcelSheetTab('Summary Report')}
                style={{
                  padding: '5px 14px',
                  fontSize: '12px',
                  fontWeight: 700,
                  borderRadius: '4px',
                  border: 'none',
                  background: excelSheetTab === 'Summary Report' ? 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))' : 'transparent',
                  color: excelSheetTab === 'Summary Report' ? '#000000' : '#94a3b8',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                Summary Report
              </button>
              <button
                type="button"
                onClick={() => setExcelSheetTab("Standard's")}
                style={{
                  padding: '5px 14px',
                  fontSize: '12px',
                  fontWeight: 700,
                  borderRadius: '4px',
                  border: 'none',
                  background: excelSheetTab === "Standard's" ? 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))' : 'transparent',
                  color: excelSheetTab === "Standard's" ? '#000000' : '#94a3b8',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                Standard's Compliance
              </button>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/dashboard/consolidated-report')}
              style={{ borderRadius: '6px', fontWeight: 700, padding: '5px 14px', background: 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))', border: '1px solid rgba(20, 184, 166, 0.45)', color: '#000000', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', letterSpacing: '-0.01em', boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 2px 6px rgba(32, 216, 202, 0.25)' }}
              title="Open full interactive Consolidated Compliance Report with drill-down views"
            >
              <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '11px' }}></i>
              <span>Open Detailed View</span>
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadConsolidatedReport}
              disabled={downloadingReport}
              style={{ borderRadius: '6px', fontWeight: 700, padding: '5px 14px', background: 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))', border: '1px solid rgba(20, 184, 166, 0.45)', color: '#000000', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', letterSpacing: '-0.01em', boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 2px 6px rgba(32, 216, 202, 0.25)' }}
            >
              <i className="fa-solid fa-file-excel" style={{ fontSize: '13px' }}></i>
              <span>{downloadingReport ? 'Generating...' : 'Download Excel (.xlsx)'}</span>
            </Button>
          </div>
        </div>

        {/* Loading Spinner during API fetch */}
        {loadingPreviewData && (
          <div style={{ padding: '60px', textAlign: 'center', backgroundColor: '#ffffff' }}>
            <Spin tip="Loading audit report data from database..." size="large" />
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#94a3b8' }}>This may take a few seconds</div>
          </div>
        )}

        {/* Error state with retry */}
        {showError && (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff' }}>
            <div style={{ fontSize: '14px', color: '#ef4444', marginBottom: '12px' }}>⚠️ {previewError}</div>
            <button
              className="cip-btn-submit"
              onClick={() => doFetchPreviewData()}
              style={{ padding: '8px 20px', borderRadius: '6px', cursor: 'pointer' }}
            >Retry</button>
          </div>
        )}

        {/* Sheet Content 1: Summary Report */}
        {!loadingPreviewData && !previewError && excelSheetTab === 'Summary Report' && sheet1Rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ width: '70px', padding: '12px 16px', textAlign: 'center' }}>Sr. No.</th>
                  <th style={{ padding: '12px 16px' }}>Report Name</th>
                  <th style={{ width: '180px', padding: '12px 16px', textAlign: 'right' }}>Value ({currLabel})</th>
                  <th style={{ width: '140px', padding: '12px 16px', textAlign: 'right' }}>Percentage</th>
                  <th style={{ width: '110px', padding: '12px 16px', textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {sheet1Rows.map((item, idx) => {
                  const srNoStr = String(item.srNo || item.sr || '');
                  const isSub = item.isSubRow || Boolean(srNoStr.match(/^16[a-z]+/i));
                  const nameStr = String(item.name || srNoStr).toLowerCase();
                  const isParent = item.isParent || srNoStr === '16';
                  const parentSrNo = isSub ? srNoStr.replace(/[a-z]+$/i, '') : null;

                  // If this is a sub-row and its parent is NOT expanded, do not render it
                  if (isSub && parentSrNo && !expandedRowsAlt[parentSrNo]) {
                    return null;
                  }

                  const isTotal = isParent || item.isTotal || nameStr.includes('total');
                  const val = item.valueInINR !== undefined ? item.valueInINR : item.val;
                  const pct = item.percentage !== undefined ? item.percentage : item.pct;
                  const cnt = item.count;
                  const isRowExpanded = Boolean(expandedRowsAlt[srNoStr]);

                  return (
                    <tr
                      key={idx}
                      onClick={(e) => {
                        if (isParent) {
                          toggleRowExpandAlt(srNoStr, e);
                        }
                      }}
                      style={{
                        cursor: isParent ? 'pointer' : 'default',
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: isTotal ? '#f1f5f9' : (isSub ? '#ffffff' : (idx % 2 === 0 ? '#ffffff' : '#fafafa')),
                        color: isSub ? '#ef4444' : '#0f172a',
                        fontWeight: isTotal ? 700 : (isSub ? 500 : 400),
                        fontSize: isTotal ? '13px' : '12.5px'
                      }}
                    >
                      <td style={{ padding: '10px 16px', textAlign: 'center', color: '#64748b', fontWeight: isTotal ? 700 : 500 }}>
                        {item.srNo || item.sr}
                      </td>
                      <td style={{ padding: isSub ? '10px 16px 10px 32px' : '10px 16px' }}>
                        {isParent && (
                          <span
                            onClick={(e) => toggleRowExpandAlt(srNoStr, e)}
                            title={isRowExpanded ? "Click to collapse sub-rows" : "Click to expand sub-rows"}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              backgroundColor: isRowExpanded ? '#dbeafe' : '#e2e8f0',
                              color: isRowExpanded ? '#1d4ed8' : '#475569',
                              marginRight: '8px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {isRowExpanded ? <DownOutlined /> : <RightOutlined />}
                          </span>
                        )}
                        {isSub && <span style={{ color: '#ef4444', marginRight: '6px', fontWeight: 700 }}>↳</span>}
                        {item.name}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                        {val !== null && val !== undefined ? (val === 0 ? '0.00' : Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '--'}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        {pct !== null && pct !== undefined ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: '#e6faf8',
                            color: '#25dbcd',
                            fontWeight: 700,
                            fontSize: '11.5px'
                          }}>
                            {typeof pct === 'number' ? `${pct.toFixed(2)}%` : pct}
                          </span>
                        ) : '--'}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: '#334155', fontFamily: 'monospace' }}>
                        {cnt !== '--' && cnt !== null && cnt !== undefined ? Number(cnt).toLocaleString() : '--'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Sheet Content 2: Standard's */}
        {!loadingPreviewData && !previewError && excelSheetTab === "Standard's" && standardsRows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ width: '70px', padding: '12px 16px', textAlign: 'center' }}>Sr. No.</th>
                  <th style={{ padding: '12px 16px' }}>Standard Certificate</th>
                  <th style={{ width: '240px', padding: '12px 16px', textAlign: 'center' }}>File Number</th>
                  <th style={{ width: '180px', padding: '12px 16px', textAlign: 'center' }}>Status / Due Days</th>
                </tr>
              </thead>
              <tbody>
                {standardsRows.map((row, idx) => {
                  const isDue = row.due && (row.due.includes('Due') || row.due.includes('Expired'));
                  return (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: row.isSub ? '#f8fafc' : '#ffffff',
                        fontWeight: row.isParent ? 700 : 400
                      }}
                    >
                      <td style={{ padding: '10px 16px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
                        {row.sr}
                      </td>
                      <td style={{ padding: '10px 16px', color: row.isParent ? '#0f172a' : '#334155' }}>
                        {row.name}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>
                        {row.file_no || row.file || '--'}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        {row.due ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            backgroundColor: isDue ? '#fef2f2' : '#f1f5f9',
                            color: isDue ? '#dc2626' : '#64748b',
                            border: `1px solid ${isDue ? '#fca5a5' : '#e2e8f0'}`
                          }}>
                            {row.due}
                          </span>
                        ) : '--'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Inconsistency Report: inline cards stacked vertically ──
  const renderInconsistencyCards = () => {
    const cardName = 'Inconsistency';
    const rawData = summaryDashboardData[cardName];
    const serializedDates = dates?.length === 2
      ? [dayjs(dates[0]).format('YYYY-MM-DD'), dayjs(dates[1]).format('YYYY-MM-DD')]
      : null;

    if (isLoading2 && !rawData) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <Spin size="large" tip="Loading Inconsistency data..." />
        </div>
      );
    }
    if (!rawData || !rawData.dashboard_summary || !rawData.headers) {
      return (
        <div className="text-muted text-center p-4">
          No inconsistency data available. Please check your date range and IEC selection.
        </div>
      );
    }

    const baseRows = rawData.dashboard_summary;
    const headers = rawData.headers;

    const processColumns = (cols) =>
      cols.map(col => {
        const newCol = { ...col };
        delete newCol.fixed;
        delete newCol.width;
        if (newCol.children) {
          newCol.children = processColumns(newCol.children);
        } else if (newCol.textColor) {
          newCol.render = (text) => <span style={{ color: newCol.textColor, fontWeight: 'bold' }}>{text}</span>;
        }
        return newCol;
      });

    const businessUnitCol = { ...processColumns([headers[0]])[0] };
    delete businessUnitCol.fixed;
    delete businessUnitCol.width;
    businessUnitCol.render = (v) => <strong style={{ fontSize: '12px' }}>{v}</strong>;

    // headers[1] = Total column, headers[2..] = the category groups
    const totalCol = { ...processColumns([headers[1]])[0] };
    delete totalCol.fixed;
    delete totalCol.width;
    const categoryHeaders = headers.slice(2);

    return (
      <div style={{ width: '100%' }}>
        <Row className="g-3" style={{ margin: 0 }}>
          {categoryHeaders.map((category, idx) => {
            const cols = [
              businessUnitCol,
              totalCol,
              ...processColumns(category.children || [])
            ];
            const tableData = baseRows.map((item, i) => ({ ...item, key: i }));
            return (
              <Col lg={12} md={12} sm={12} key={idx} className="mb-3">
                <Card
                  style={{
                    border: `1px solid ${borderGrey}`,
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderBottom: `1px solid ${borderGrey}`,
                      borderRadius: '8px 8px 0 0',
                      padding: '10px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: '100%'
                    }}
                  >
                    <div style={{ width: '4px', height: '14px', backgroundColor: '#25dbcd', borderRadius: '2px', flexShrink: 0 }} />
                    <span style={{ fontWeight: '700', fontSize: '13.5px', color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {category.title}
                    </span>
                  </div>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <AntTable
                        className="dash-summary-table m-0"
                        columns={cols}
                        dataSource={tableData}
                        pagination={false}
                        size="small"
                        bordered
                        rowClassName={(record) =>
                          record.business_unit === 'Grand Total' ? 'ant-table-summary-row' : ''
                        }
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Navigation link to the full detailed report */}
        <div style={{ marginTop: '12px', paddingLeft: '4px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard/reports/inconsistency-report', { state: { passedDates: serializedDates } });
            }}
            href="#"
            style={{
              background: 'linear-gradient(135deg, #818CF8 0%, #4F46E5 100%)',
              color: '#fff',
              padding: '7px 18px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '13px',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(99,102,241,0.28)',
              border: 'none'
            }}
          >
            <ArrowRightOutlined /> Go to Full Inconsistency Report
          </a>
        </div>
      </div>
    );
  };

  const renderAdminSummary = (cardName) => {
    const rawData = summaryDashboardData[cardName];
    if (isLoading2 && !rawData) {
      return (
        <div className="d-flex justify-content-center p-4">
          <Spin size="large" tip="Loading data..." />
        </div>
      );
    }
    if (!rawData) {
      return <div className="text-muted text-center p-4">No data available for this selection.</div>;
    }

    const reportTitleMap = {
      'Summary-Imp': 'Import Summary Report',
      'Summary-Exp': 'Export Summary Report',
      'EDPMS': 'EDPMS Export Pendency Report',
      'IDPMS': 'IDPMS Import Pendency Report',
      'DBK-AIR': 'Duty Drawback (DBK-AIR) Report',
      'IGST-Refund': 'IGST Refund Status Report',
      'RODTEP-Exp': 'RoDTEP Export Status & Liability Report',
      'Rodtep-Util': 'RoDTEP Scrip Ledger & Utilization Report',
      'Bond-Utilization': 'Bond Utilization & Liability Report',
      'EPCG-Auth-EO': 'EPCG Obligation & License Report',
      'AdvAuth-EO': 'Advance Authorization Export EO Report',
      'AdvAuth-Utilization': 'Advance Authorization Import Utilization Report',
      'AdvAuthExcessImportLiab': 'Adv Auth Excess Import Liability Report',
      'TAT-ImportClearance': 'Clearance Time (Import) Report',
      'TAT-ExportClearance': 'Clearance Time (Export) Report',
      'Inconsistency': 'HSN & Tax Inconsistency Report',
      'FTA-Opportunity': 'FTA Opportunity Report'
    };
    const titleText = reportTitleMap[cardName] || `${cardName} Report`;

    const downloadMenu = (
      <Menu>
        <Menu.Item
          key="summary_dl"
          onClick={() => handleDownloadReport(cardName, 'ALL', 'Summary')}
        >
          <div style={{ padding: '2px 4px', cursor: 'pointer' }}>
            <i className="fa-solid fa-file-excel text-success me-2"></i>
            <strong>Summary Report</strong> (.xlsx)
          </div>
        </Menu.Item>
        <Menu.Item
          key="detail_dl"
          onClick={() => handleDownloadReport(cardName, 'Detail', 'Detailed')}
        >
          <div style={{ padding: '2px 4px', cursor: 'pointer' }}>
            <i className="fa-solid fa-table-list text-primary me-2"></i>
            <strong>Detailed Report</strong> (.xlsx)
          </div>
        </Menu.Item>
      </Menu>
    );

    const toggleColumn = (key) => {
      setVisibleColumns(prev => {
        if (prev.includes(key)) {
          if (prev.length <= 1) {
            message.warning('At least one column must remain visible.');
            return prev;
          }
          return prev.filter(c => c !== key);
        }
        return [...prev, key];
      });
    };

    const columnPopoverContent = (
      <div style={{ width: '220px', padding: '4px 0' }}>
        <div
          className="d-flex align-items-center justify-content-between pb-2 mb-2"
          style={{ borderBottom: '1px solid #f1f5f9' }}
        >
          <span style={{ fontWeight: 700, fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Table Columns
          </span>
          {visibleColumns.length < 4 && (
            <span
              onClick={() => setVisibleColumns(['description', 'count', 'amount', 'percentage'])}
              style={{ fontSize: '11px', color: '#2563eb', fontWeight: 600, cursor: 'pointer' }}
            >
              Reset All
            </span>
          )}
        </div>

        <div className="d-flex flex-column gap-2">
          {[
            { key: 'description', label: 'Description / Particulars', icon: 'fa-align-left' },
            { key: 'count', label: 'Count (Units)', icon: 'fa-hashtag' },
            { key: 'amount', label: selectedCurrency === 'USD' ? 'Amount ($)' : 'Amount (₹)', icon: 'fa-indian-rupee-sign' },
            { key: 'percentage', label: 'Percentage (%)', icon: 'fa-percent' }
          ].map(col => {
            const isChecked = visibleColumns.includes(col.key);
            return (
              <div
                key={col.key}
                onClick={() => toggleColumn(col.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  backgroundColor: isChecked ? '#f8fafc' : '#ffffff',
                  border: `1px solid ${isChecked ? '#cbd5e1' : '#f1f5f9'}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className={`fa-solid ${col.icon}`} style={{ fontSize: '11px', color: isChecked ? '#3b82f6' : '#94a3b8', width: '14px' }}></i>
                  <span style={{ fontSize: '12.5px', fontWeight: isChecked ? 600 : 400, color: isChecked ? '#0f172a' : '#64748b' }}>
                    {col.label}
                  </span>
                </div>
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleColumn(col.key);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );

    const targetReportRouteMap = {
      // 'Summary-Imp': '/dashboard/report-templates/create?__report__=REPORT_BOE',
      // 'Summary-Exp': '/dashboard/report-templates/create?__report__=REPORT_SHB',
      'EDPMS': '/dashboard/reports/brc-report',
      'IDPMS': '/dashboard/report-templates/create?__report__=REPORT_BOE',
      'DBK-AIR': '/dashboard/reports/drawback-report',
      'IGST-Refund': '/dashboard/reports/igst-report',
      'RODTEP-Exp': '/dashboard/reports/rodtep-export-report',
      'Rodtep-Util': '/dashboard/reports/rodtep-import-report',
      'AdvAuth-EO': '/dashboard/reports/advauth-export-report',
      'AdvAuth-Utilization': '/dashboard/reports/advauth-import-report',
      'AdvAuthExcessImportLiab': '/dashboard/reports/advauth-excess-import-report',
      'EPCG-Auth-EO': '/dashboard/reports/epcg-report',
      'TAT-ImportClearance': '/dashboard/reports/clearance-time-import-report',
      'TAT-ExportClearance': '/dashboard/reports/clearance-time-export-report',
      'Inconsistency': '/dashboard/reports/inconsistency-report',
      'FTA-Opportunity': '/dashboard/reports/fta-opp-report',
      'FTA-Applicability': '/dashboard/reports/fta-applicability-report',
      'Bond-Utilization': '/dashboard/reports/bond-report'
    };
    const targetRoute = targetReportRouteMap[cardName];

    const cardHeader = (
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2.5"
        style={{
          backgroundColor: '#f8fafc',
          borderBottom: `1px solid ${borderGrey}`,
          borderRadius: '8px 8px 0 0',
          minHeight: '52px'
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="fa-solid fa-table-cells text-primary" style={{ fontSize: '14px' }}></i>
          <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
            {titleText}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
          {cardName === 'FTA-Opportunity' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Remark :</span>
                <Select
                  value={categoryRemarkFilter}
                  onChange={(value) => {
                    setCategoryRemarkFilter(value);
                    getSummaryDashboard("FTA-Opportunity", null, undefined, value);
                  }}
                  style={{ width: 130, height: 32 }}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  <Select.Option value="FTA Applicable and Availed">Availed</Select.Option>
                  <Select.Option value="FTA Applicable but Not Availed">Not Availed</Select.Option>
                </Select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>COO = COS :</span>
                <Select
                  value={cooCosFilter}
                  onChange={(value) => {
                    setCooCosFilter(value);
                    getSummaryDashboard("FTA-Opportunity", null, value);
                  }}
                  style={{ width: 90, height: 32 }}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  <Select.Option value="TRUE">True</Select.Option>
                  <Select.Option value="FALSE">False</Select.Option>
                </Select>
              </div>
            </div>
          )}
          {targetRoute && (
            <button
              type="button"
              onClick={() => {
                const serializedDates = dates?.length === 2 ? [dayjs(dates[0]).format('YYYY-MM-DD'), dayjs(dates[1]).format('YYYY-MM-DD')] : null;
                navigate(targetRoute, { state: { passedDates: serializedDates } });
              }}
              style={{
                background: 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))',
                border: '1px solid rgba(20, 184, 166, 0.45)',
                color: '#000000',
                fontWeight: '700',
                letterSpacing: '-0.01em',
                fontSize: '12px',
                borderRadius: '6px',
                height: 32,
                padding: '0 14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 2px 6px rgba(32, 216, 202, 0.25)',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap'
              }}
            >
              Go to report summary <ArrowRightOutlined style={{ fontSize: '11px' }} />
            </button>
          )}
        </div>
      </div>
    );

    const isMultiMode = !rawData.dashboard_summary && (rawData.All || rawData.Sea || rawData.Air || rawData.Land);

    const renderTable = (headers, data, title = null) => {
      // Make total/summary rows bold across all reports
      const isBoldRow = (record) => {
        const desc = String(record?.description || record?.remark || record?.final_remark || record?.particulars || record?.category || record?.status || record?.type || record?.name || '').trim().toLowerCase();
        return desc.includes('total') || Boolean(record?.isTotal || record?.is_total);
      };

      const textDataIndexes = [
        'description', 'remark', 'final_remark', 'business_unit', 'lic_no',
        'license_no', 'bond_no', 'status', 'remarks', 'category', 'scheme',
        'particulars', 'type', 'hsn_consistency_remark'
      ];
      const hasChildren = headers && headers.some(h => h.children && h.children.length > 0);

      const processNestedHeaders = (cols) => {
        return cols.map(c => {
          if (c.children && c.children.length > 0) {
            return {
              ...c,
              children: processNestedHeaders(c.children)
            };
          }
          return {
            ...c,
            render: (text, record) => {
              const bold = isBoldRow(record);
              const textColor = c.textColor || (record ? record.color : null);
              return (
                <span style={{ color: textColor || 'inherit', fontSize: "13px", fontWeight: bold ? 800 : 400 }}>
                  {text !== null && text !== undefined ? text : '--'}
                </span>
              );
            }
          };
        });
      };

      let adjustedHeaders = [];
      if (hasChildren) {
        adjustedHeaders = processNestedHeaders(headers);
      } else {
        // Normalize column titles, assign colKeys, and enforce explicit order: Description -> Years -> Count -> Value (INR/USD) -> Percentage (%)
        let normalizedHeaders = headers.map(h => {
          const idx = (h.dataIndex || '').toLowerCase();
          let colKey = 'description';
          let title = h.title;
          if (idx === 'summary' || idx === 'count') {
            colKey = 'count';
            title = h.title || 'Count';
          } else if (idx === 'amount' || idx === 'amount_inr' || idx === 'value' || idx === 'amount_usd' || idx === 'total_fob_value' || idx === 'eo_fulfilled_val' || idx === 'bal_eo_val' || idx === 'bal_eo_value' || idx === 'rodtep_scrip_amount' || idx === 'balance_amt' || idx === 'bcd_availed' || idx === 'bcd_missed_opp' || idx === 'total_eligible' || idx === 'duty_saved' || idx === 'eo_prescribed') {
            colKey = 'amount';
            title = h.title || 'Amount';
          } else if (idx.includes('percent')) {
            // e.g. FTA-Opportunity's availed_percentage / missed_percentage
            colKey = 'percentage';
            title = h.title || 'Percentage (%)';
          } else if (/^\d{4}$/.test(idx)) {
            colKey = 'years';
          }
          return { ...h, title, colKey };
        });

        const getHeaderOrderKey = (h) => {
          if (h.colKey === 'description') return 0;
          if (h.colKey === 'years') return 1;
          if (h.colKey === 'count') return 2;
          if (h.colKey === 'amount') return 3;
          if (h.colKey === 'percentage') return 4;
          return 5;
        };

        normalizedHeaders.sort((a, b) => getHeaderOrderKey(a) - getHeaderOrderKey(b));

        // Filter headers based on user-selected visibleColumns state (years map under count/description)
        normalizedHeaders = normalizedHeaders.filter(h =>
          h.colKey === 'years' ? visibleColumns.includes('count') : visibleColumns.includes(h.colKey)
        );

        adjustedHeaders = normalizedHeaders.map((header, index) => {
          if (index === 0 || textDataIndexes.includes(header.dataIndex)) {
            return {
              ...header,
              render: (text, record) => {
                const bold = isBoldRow(record);
                if (!text) {
                  return <span style={{ color: record.color, fontSize: '13.5px', fontWeight: bold ? 800 : 400 }}>{text || ''}</span>;
                }
                return (
                  <span
                    style={{ color: record.color, cursor: 'pointer', fontSize: '13.5px', fontWeight: bold ? 800 : 400 }}
                    title={`Click to download ${cardName} - ${String(text).replace(/↳/g, '').trim()} report`}
                    onClick={async () => {
                      const iec = activeIEC || localStorage.getItem('iec_no') || 'ALL';
                      const fromDate = dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
                      const toDate = dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;
                      // Nested rows (e.g. FTA BU → plant / category-remark children) render
                      // their label with a "↳" tree glyph and indent spaces. Strip that
                      // decoration so the backend receives the raw category/BU/plant value.
                      const category = String(text).replace(/↳/g, '').replace(/\s+/g, ' ').trim();
                      try {
                        message.loading({ content: `Downloading ${cardName} - ${category}...`, key: 'rpt_dl', duration: 0 });
                        // For TAT multi-mode cards, `title` carries the active mode tab
                        // (All / Sea / Air / Land) so the export scopes to that mode.
                        const tatMode = ['All', 'Sea', 'Air', 'Land', 'SEZ'].includes(title) ? title : undefined;
                        // FTA Opportunity carries a COO=COS toggle (All / True / False);
                        // pass it through so the download matches the on-screen figures.
                        const cooCos = cardName === 'FTA-Opportunity' ? cooCosFilter : undefined;
                        const response = await downloadDashboardReportService(cardName, iec, category, fromDate, toDate, tatMode, cooCos);
                        if (response && response.data) {
                          const blob = new Blob([response.data], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                          });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          const safeReport = String(cardName).replace(/ /g, '_');
                          const safeCategory = String(category).replace(/ /g, '_').replace(/\//g, '-');
                          link.setAttribute('download', `${safeReport}_${safeCategory}_${iec}.xlsx`);
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                          message.success({ content: 'Downloaded successfully!', key: 'rpt_dl' });
                        } else {
                          message.error({ content: 'Download failed. Please try again.', key: 'rpt_dl' });
                        }
                      } catch (err) {
                        console.error('Report download error:', err);
                        message.error({ content: 'Download failed. Please try again.', key: 'rpt_dl' });
                      }
                    }}
                  >
                    {text} <i className="fa-solid fa-download ml-1" style={{ fontSize: '10px', opacity: 0.6 }}></i>
                  </span>
                );
              }
            };
          }

          const isPendencyReport = cardName === 'Summary-Imp' || cardName === 'Summary-Exp';
          const monetaryColumns = [
            'total_amount', 'amount', 'amount_inr', 'total_fob_value', 'eo_fulfilled_val', 'bal_eo_val', 'bal_eo_value',
            'rodtep_scrip_amount', 'balance_amt', 'bcd_availed', 'bcd_missed_opp', 'total_eligible', 'duty_saved', 'eo_prescribed'
          ];

          const isMonetary = monetaryColumns.includes(header.dataIndex);

          if (selectedCurrency === 'USD') {
            if (header.dataIndex === 'summary' && isPendencyReport) {
              return {
                ...header,
                title: cardName === 'Summary-Imp' ? 'BOE Count' : 'SHB Count',
                render: (text, record) => (
                  <span style={{ color: record.color, fontSize: "13.5px", fontWeight: isBoldRow(record) ? 800 : 400 }}>
                    {text}
                  </span>
                )
              };
            }

            if (isMonetary) {
              const usdKey = `${header.dataIndex}_usd`;
              const rawTitle = header.title || 'Amount';
              const baseTitle = (rawTitle === 'Value (INR)' || rawTitle === 'Value (USD)') ? 'Amount' : rawTitle.replace(/ \(₹\)/g, '').replace(/ \(INR\)/g, '').replace(/ \(\$\)/g, '').replace(/ \(USD\)/g, '');
              const formattedTitle = baseTitle.endsWith('($)') ? baseTitle : `${baseTitle} ($)`;
              return {
                ...header,
                title: formattedTitle,
                dataIndex: usdKey,
                render: (text, record) => (
                  <span style={{ color: record.color, fontSize: "13.5px", fontWeight: isBoldRow(record) ? 800 : 400 }}>
                    ${Number(record[usdKey] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )
              };
            }

            if (!isPendencyReport && /^\d{4}$/.test(header.dataIndex)) {
              return {
                ...header,
                dataIndex: `${header.dataIndex}_amount_usd`,
                render: (text, record) => (
                  <span style={{ color: record.color, fontSize: "13.5px", fontWeight: isBoldRow(record) ? 800 : 400 }}>
                    ${Number(text || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )
              };
            }
          } else {
            // INR Mode
            if (isMonetary) {
              const rawTitle = header.title || 'Amount';
              const baseTitle = (rawTitle === 'Value (INR)' || rawTitle === 'Value (USD)') ? 'Amount' : rawTitle.replace(/ \(₹\)/g, '').replace(/ \(INR\)/g, '').replace(/ \(\$\)/g, '').replace(/ \(USD\)/g, '');
              const formattedTitle = baseTitle.endsWith('(₹)') ? baseTitle : `${baseTitle} (₹)`;
              return {
                ...header,
                title: formattedTitle,
                render: (text, record) => (
                  <span style={{ color: record.color, fontSize: "13.5px", fontWeight: isBoldRow(record) ? 800 : 400 }}>
                    ₹{Number(text || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )
              };
            }
          }

          const isPercentageColumn = (index) =>
            index && (index.toLowerCase().includes("percentage") || index.toLowerCase().includes("percent"));

          return {
            ...header,
            render: (text, record) => (
              <span style={{ color: record.color, fontSize: "13.5px", fontWeight: isBoldRow(record) ? 800 : 400 }}>
                {text}{isPercentageColumn(header.dataIndex) ? '%' : ''}
              </span>
            )
          };
        });
      }

      return (
        <div className="mt-1 mb-2" key={title || 'single'}>
          {title && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <div style={{ width: '4px', height: '14px', backgroundColor: '#25DBCD', borderRadius: '2px' }}></div>
              <span style={{ fontWeight: '700', fontSize: '13.5px', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Mode: {title}
              </span>
            </div>
          )}
          <div className="dash-summary-wrap" style={{ overflowX: 'auto' }}>
            <AntTable
              className="dash-summary-table m-0"
              columns={adjustedHeaders}
              dataSource={data}
              pagination={false}
              size="small"
              loading={isLoading2}
              scroll={hasChildren || cardName === 'Inconsistency' ? { x: 'max-content' } : undefined}
              rowClassName={(record) => isBoldRow(record) ? 'summary-bold-row' : ''}
            />
          </div>
        </div>
      );
    };

    let tableContent = null;
    if (isMultiMode) {
      const availableModes = ['All', 'Sea', 'Air', 'Land'].filter(m => rawData[m]);
      const currentMode = availableModes.includes(selectedMode) ? selectedMode : availableModes[0];
      const modeData = rawData[currentMode];

      tableContent = (
        <div className="multi-mode-container mt-2">
          <div className="mb-3">
            <Radio.Group
              value={currentMode}
              onChange={(e) => setSelectedMode(e.target.value)}
              buttonStyle="solid"
              size="small"
            >
              {availableModes.map(mode => (
                <Radio.Button key={mode} value={mode}>{mode}</Radio.Button>
              ))}
            </Radio.Group>
          </div>
          {modeData && modeData.headers && modeData.dashboard_summary && (
            renderTable(modeData.headers, modeData.dashboard_summary, currentMode)
          )}
        </div>
      );
    } else if (rawData.headers && rawData.dashboard_summary) {
      tableContent = renderTable(rawData.headers, rawData.dashboard_summary);
    } else {
      tableContent = <div className="text-muted text-center p-4">Invalid table format.</div>;
    }

    return (
      <div style={{ width: '100%' }}>
        {cardHeader}
        <div style={{ padding: '4px 8px' }}>
          {tableContent}
          <div style={{ marginTop: '10px', paddingLeft: '5px', paddingBottom: '8px' }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDownloadPendencySummary(cardName);
              }}
              style={{
                backgroundColor: '#25dbcd',
                color: '#0f172a',
                padding: '6px 16px',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '13px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 8px rgba(37, 219, 205, 0.35)',
                transition: 'all 0.15s ease'
              }}
            >
              <ArrowRightOutlined /> Download report summary
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Contextual chart for the right-hand panel in the CIP tab ──────────────
  const REPORT_TYPE_TO_PAGES_MAP = {
    "BRC Report": ['EDPMS', 'IDPMS'],
    "DBK AIR Status and Liability": ['DBK-AIR', 'Summary-Exp'],
    "IGST Status": ['IGST-Refund'],
    "RODTEP Status /Scrip and Liability": ['RODTEP-Exp'],
    "RODTEP Scrip Ledger": ['Rodtep-Util'],
    "Clearance Time Import": ['TAT-ImportClearance'],
    "Clearance Time Export": ['TAT-ExportClearance'],
    "Bond Report": ['Bond-Utilization'],
    "EPCG Report": ['EPCG-Auth-EO'],
    "Advance Auth Export": ['AdvAuth-EO'],
    "Advance Auth Import": ['AdvAuth-Utilization'],
    "Adv Auth Excess Imp": ['AdvAuthExcessImportLiab'],
    "Inconsistency Report": ['Inconsistency'],
    "FTA Opp": ['FTA-Opportunity'],
    "FTA": ['FTA-Opportunity'],
    "FTA Applicability": ['FTA-Opportunity'],
    "Summary-Imp": ['Summary-Imp'],
    "Summary-Exp": ['Summary-Exp']
  };

  const isChartRelatedToPage = (chart, cardName) => {
    if (!chart) return false;
    if (chart.page_name === cardName) return true;
    if (chart.report_type === cardName) return true;
    if (chart.report_type && chart.report_type.startsWith('D2D Import')) return cardName === 'Summary-Imp';
    if (chart.report_type && chart.report_type.startsWith('D2D Export')) return cardName === 'Summary-Exp';
    if (chart.report_type === 'Duty Savings - Import') return cardName === 'Summary-Imp';
    if (chart.report_type === 'Duty Savings - Export') return cardName === 'Summary-Exp';
    const mappedPages = REPORT_TYPE_TO_PAGES_MAP[chart.report_type];
    if (mappedPages && mappedPages.includes(cardName)) return true;
    return false;
  };

  const renderSummaryChart = (cardName) => {
    const rawData = summaryDashboardData[cardName];

    // Active dashboard duration — charts must reflect the currently selected range,
    // not the range saved with the chart when it was created.
    const chartDateFrom = dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
    const chartDateTo = dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;

    const chartCardStyle = {
      border: `1px solid ${borderGrey}`,
      borderRadius: '8px',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    };

    const chartHeaderStyle = {
      backgroundColor: '#f8f9fa',
      borderBottom: `1px solid ${borderGrey}`,
      borderRadius: '8px 8px 0 0',
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    };

    const pageCharts = charts.filter(c => c.is_active !== false && isChartRelatedToPage(c, cardName));
    const pinnedChart = pageCharts.find(c => c.show_on_dashboard && c.page_name === cardName)
      || charts.find(c => c.page_name === cardName && c.show_on_dashboard && c.is_active !== false);

    // The current chart's name is already shown as the card title, so this control is
    // purely a switcher — it never displays the selected value (avoids the duplicate name).
    const chartSelectorDropdown = pageCharts.length > 0 && (
      <Select
        className="cip-chart-switch"
        style={{ width: '150px', flexShrink: 0 }}
        placeholder="Switch chart"
        value={undefined}
        onChange={(val) => handlePinChart(val, cardName)}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        suffixIcon={<i className="fa-solid fa-arrow-right-arrow-left" style={{ fontSize: '10px' }} />}
        options={[
          ...(pinnedChart ? [{ value: 'unpin', label: 'Unpin Current Chart' }] : []),
          ...pageCharts.map(c => ({
            value: c.chart_id,
            label: c.chart_name
          }))
        ]}
      />
    );

    if (pinnedChart) {
      return (
        <div style={chartCardStyle}>
          <div style={chartHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
              <div style={{ width: '4px', height: '14px', backgroundColor: '#25dbcd', borderRadius: '2px', flexShrink: 0 }} />
              <span style={{ fontWeight: '700', fontSize: '13.5px', color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pinnedChart.chart_name}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {chartSelectorDropdown}
              <button type="button" onClick={() => summaryChartRef.current?.download('pdf')} title="Download PDF"
                style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <DownloadOutlined style={{ fontSize: '15px' }} />
              </button>
            </div>
          </div>
          <div style={{ padding: '10px', flex: 1, minHeight: '280px' }}>
            <GraphView
              ref={summaryChartRef}
              chartType={pinnedChart.chart_type}
              chartId={pinnedChart.chart_id}
              config={pinnedChart}
              height={280}
              compact={true}
              columnTitles={pinnedChart.column_titles || {}}
              showMetricOverlay={false}
              showDownload={false}
              overrideDateRange={true}
              dateFrom={chartDateFrom}
              dateTo={chartDateTo}
            />
          </div>
        </div>
      );
    }

    const rawRows = Array.isArray(rawData)
      ? rawData
      : (rawData?.dashboard_summary || []);

    const validRows = rawRows
      .filter(r => !/total|grand/i.test(r.description || r.business_unit || r.final_remark || ''))
      .map(r => ({
        name: r.description || r.business_unit || r.final_remark || 'BU',
        Value: Number(r.amount || r.amount_inr || r.summary || r.total || 0)
      }))
      .filter(r => r.Value > 0);

    return (
      <div style={chartCardStyle}>
        <div style={chartHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
            <div style={{ width: '4px', height: '14px', backgroundColor: '#25dbcd', borderRadius: '2px', flexShrink: 0 }} />
            <span style={{ fontWeight: '700', fontSize: '13.5px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cardName} Breakdown
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {chartSelectorDropdown}
            {validRows.length > 0 && (
              <button type="button" onClick={() => summaryChartRef.current?.download('pdf')} title="Download PDF"
                style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <DownloadOutlined style={{ fontSize: '15px' }} />
              </button>
            )}
          </div>
        </div>
        <div style={{ padding: '10px', flex: 1, minHeight: '280px' }}>
          {validRows.length > 0 ? (
            <GraphView
              ref={summaryChartRef}
              chartType="bar"
              data={validRows}
              height={270}
              compact={true}
              showDataLabels={true}
              showDownload={false}
            />
          ) : (
            <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '60px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
              <p style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>No summary chart data</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleImpDutySearch = async (e) => {
    e.preventDefault();
    if (!impDutyHscode) {
      message.error("Please enter HS Code");
      return;
    }
    setImpDutyLoading(true);
    try {
      if (impDutyAssessableValue) {
        const result = await searchTradeData(impDutyHscode, impDutyAssessableValue);
        navigate('/dashboard/trade-search', {
          state: { hscode: impDutyHscode, assessableValue: impDutyAssessableValue, fullSearchResult: result }
        });
      } else {
        const result = await searchHscodeOnly(impDutyHscode);
        navigate('/dashboard/trade-search', {
          state: { hscode: impDutyHscode, generalData: result }
        });
      }
    } catch (error) {
      message.error(typeof error === 'string' ? error : 'Search failed');
    } finally {
      setImpDutyLoading(false);
    }
  };

  const renderImpDutyRateSearch = () => {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto' }}>
        <Card style={{ border: `1px solid ${borderGrey}` }}>
          <Card.Header style={{ backgroundColor: '#f8f9fa' }}>
            <h5 className="mb-0 font-weight-bold" style={{ fontSize: '14px' }}>Import Duty Rate Lookup</h5>
          </Card.Header>
          <Card.Body>
            <BootstrapForm onSubmit={handleImpDutySearch}>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label className="font-weight-bold" style={{ fontSize: '12px' }}>Enter HS Code (8-digit preferred)</BootstrapForm.Label>
                <AutoComplete
                  value={impDutyHscode}
                  options={impDutyHscodeOptions}
                  onChange={(val) => { setImpDutyHscode(val); fetchImpDutyHscodeOptions(val) }}
                  onSelect={(val) => { setImpDutyHscode(val); setImpDutyHscodeOptions([]) }}
                  placeholder="e.g. 84713010"
                  style={{ width: '100%' }}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label className="font-weight-bold" style={{ fontSize: '12px' }}>Assessable Value (INR)</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="number"
                  value={impDutyAssessableValue}
                  onChange={(e) => setImpDutyAssessableValue(e.target.value)}
                  placeholder="Enter value"
                />
              </BootstrapForm.Group>
              <Button type="submit" disabled={impDutyLoading} className="w-100 custom-button" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {impDutyLoading ? <Spin size="small" style={{ marginRight: '8px' }} /> : <SearchOutlined style={{ marginRight: '8px' }} />} Calculate Duty & Search Trade Data
              </Button>
            </BootstrapForm>
          </Card.Body>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    // A sub-tab switch that also changes the date range produces two renders:
    // this render still holds the previous tab's `dates` (e.g. the 5-year
    // Summary range → from_date 2022), then the date-sync effect updates
    // `dates` and re-triggers this effect with the correct range. Skip the
    // first (stale) run so only one summary request is made, with the right dates.
    if (tabSwitchPendingRef.current) {
      tabSwitchPendingRef.current = false
      return
    }
    if (activeTab === 'CIP' && selectedSubTab) {
      if (isCompareActive) {
        fetchCompareData(selectedSubTab)
      } else {
        getSummaryDashboard(selectedSubTab)
      }
    } else if (activeTab === 'STANDARD' && selectedStatementType) {
      getSummaryDashboard(normalizeStatementType(selectedStatementType))
    }
  }, [activeTab, selectedSubTab, selectedStatementType, activeIEC, dates, compareIecs.join(',')])

  useEffect(() => {
    window.alternateActiveTab = activeTab;
    localStorage.setItem('alternateActiveTab', activeTab);
    localStorage.setItem('selectedStatementType', selectedStatementType);
    localStorage.setItem('selectedSubTab', selectedSubTab);
    window.dispatchEvent(new CustomEvent('alternateTabChanged', { detail: activeTab }));

    const desiredSlug = TAB_SLUGS[activeTab];
    if (desiredSlug) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        let changed = false;

        if (next.get('tab') !== desiredSlug) {
          next.set('tab', desiredSlug);
          changed = true;
        }

        if (activeTab === 'STANDARD' && selectedStatementType) {
          const compSlug = selectedStatementType.toLowerCase();
          if (next.get('compliance') !== compSlug) {
            next.set('compliance', compSlug);
            changed = true;
          }
        } else if (next.has('compliance')) {
          next.delete('compliance');
          changed = true;
        }

        if (activeTab === 'CIP' && selectedSubTab) {
          if (next.get('subtab') !== selectedSubTab) {
            next.set('subtab', selectedSubTab);
            changed = true;
          }
        } else if (next.has('subtab')) {
          next.delete('subtab');
          changed = true;
        }

        return changed ? next : prev;
      }, { replace: true });
    }
  }, [activeTab, selectedStatementType, selectedSubTab]);

  useEffect(() => {
    const handleExternalTabChange = (e) => {
      if (e.detail && e.detail !== activeTab) {
        setActiveTab(e.detail);
      }
    };
    window.addEventListener('setAlternateActiveTab', handleExternalTabChange);
    return () => {
      window.removeEventListener('setAlternateActiveTab', handleExternalTabChange);
    };
  }, [activeTab]);

  // ── Tab ⇄ URL sync (deep-linkable tabs: handle back/forward navigation) ──────
  useEffect(() => {
    const mappedTab = SLUG_TABS[searchParams.get('tab')];
    if (mappedTab && mappedTab !== activeTab) {
      setActiveTab(mappedTab);
    }
    const compFromUrl = searchParams.get('compliance') || searchParams.get('statement');
    if (compFromUrl) {
      const statementList = ['EH', 'AEO', 'RCMC', 'FSP', 'DSC', 'Returns', 'FTA', 'CCR'];
      const matched = statementList.find(s => s.toLowerCase() === compFromUrl.toLowerCase());
      if (matched && matched !== selectedStatementType) {
        setSelectedStatementType(matched);
      }
    }
    const subtabFromUrl = searchParams.get('subtab');
    if (subtabFromUrl && subtabFromUrl !== selectedSubTab) {
      setSelectedSubTab(subtabFromUrl);
    }
  }, [searchParams]);

  // Handle sidebar action clicks
  const handleTabChange = (tabName) => {
    setActiveTab(tabName)
    setSelectedModule(null)
  }

  // Toolbar Render Helper (replacing sidebar)
  // Shared compact currency toggle — sits inline with the Duration filter
  const renderCurrencyToggle = () => (
    <Radio.Group
      value={selectedCurrency}
      onChange={(e) => setSelectedCurrency(e.target.value)}
      buttonStyle="solid"
      size="small"
    >
      <Radio.Button value="INR">INR</Radio.Button>
      <Radio.Button value="USD">USD</Radio.Button>
    </Radio.Group>
  );

  const renderToolbar = () => {
    const toolbarStyle = {
      backgroundColor: 'var(--cip-surface)',
      border: '1px solid var(--cip-border)',
      borderRadius: '12px',
      boxShadow: 'var(--cip-shadow-sm)',
      padding: '8px 16px',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '15px'
    }

    if (activeTab === 'HOME') {
      return (
        <div style={toolbarStyle}>
          <div className="d-flex align-items-center gap-3">
            <span className="font-weight-bold text-dark" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Home Controls</span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/dashboard/user-management')}
              style={{ borderRadius: '6px', fontSize: '12px', borderColor: '#ccc', color: '#4b5563' }}
            >
              <UserOutlined /> Manage Users
            </Button>
          </div>
          {renderCurrencyToggle()}
        </div>
      )
    }

    if (activeTab === 'CIP') {
      return (
        <div style={toolbarStyle}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="font-weight-bold text-dark" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CIP Control</span>
            {renderDateFilter()}
          </div>
          {renderCurrencyToggle()}
        </div>
      )
    }

    if (activeTab === 'D2D') {
      return (
        <div style={toolbarStyle}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="font-weight-bold text-dark" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>D2D Intelligence</span>
            {renderDateFilter()}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/dashboard/reports')}
              style={{ borderRadius: '6px', fontSize: '12px', borderColor: '#ccc', color: '#4b5563' }}
            >
              <FileTextOutlined /> Standard Reports
            </Button>
            {renderCurrencyToggle()}
          </div>
        </div>
      )
    }

    if (activeTab === 'PRESENT') {
      return (
        <div style={toolbarStyle}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="font-weight-bold text-dark" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Presentation</span>
            {(userDetails.role === 'superadmin' || userDetails.role === 'group_head') && (
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary" style={{ fontSize: '11px', fontWeight: '600' }}>Select Company:</span>
                <Select
                  value={activeIEC}
                  onChange={handleIECChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  popupClassName="cip-select-dropdown"
                  placeholder="Search company…"
                  style={{ width: 260 }}
                  size="middle"
                >
                  {IECOptions.map(opt => (
                    <Select.Option key={opt.iec_no} value={opt.iec_no}>{opt.fullname || opt.iec_no}</Select.Option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          <span className="text-secondary" style={{ fontSize: '12px' }}>Use ← → or the controls to move between charts</span>
        </div>
      )
    }

    if (activeTab === 'STATEMENTS') {
      const currentStatementObj = statementsList.find(s => s.key === selectedStatementType) || statementsList[0];

      return (
        <div style={toolbarStyle}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="font-weight-bold text-dark" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Statements & Uploads</span>
            <Dropdown
              menu={{
                items: dropdownMenuItems,
                onClick: ({ key }) => handleSelectStatement(key),
                style: {
                  maxHeight: '260px',
                  overflowY: 'auto',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  padding: '4px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #e2e8f0',
                  width: '210px'
                }
              }}
              trigger={['click']}
            >
              <Button
                style={{
                  backgroundColor: '#e6faf8',
                  border: '1.5px solid #25DBCD',
                  color: '#25DBCD',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  borderRadius: '8px',
                  padding: '6px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                size="sm"
              >
                Statement: {currentStatementObj?.title || selectedStatementType} ▾
              </Button>
            </Dropdown>
            {renderDateFilter()}
          </div>
          {renderCurrencyToggle()}
        </div>
      )
    }

    return null
  }

  // Header Sub-header status bar Render Helper
  const renderSubHeader = () => {
    if (activeTab === 'CIP') {
      const categories = [
        { key: 'Summary', label: 'Summary' },
        { key: 'Duty Credit and Refunds', label: 'Duty Credit and Refunds' },
        { key: 'Duty Exemption and Obligations', label: 'Duty Exemption and Obligations' },
        { key: 'Smart Features', label: 'Smart Features' },
        { key: 'Audit', label: 'Audit' }
      ]

      return (
        <div className="cip-maintabs">
          {categories.map((cat) => (
            <div
              key={cat.key}
              role="tab"
              aria-selected={selectedCategory === cat.key}
              className={`cip-maintab ${selectedCategory === cat.key ? 'is-active' : ''}`}
              onClick={() => {
                setSelectedCategory(cat.key)
                const subTabs = categorySubTabs[cat.key] || [];
                const firstAllowedTab = subTabs.find(tab => {
                  const isDisabled = allowedComplianceTabs !== null && !allowedComplianceTabs.has(tab.key);
                  return !isDisabled;
                });
                setSelectedSubTab(firstAllowedTab ? firstAllowedTab.key : (subTabs[0]?.key || null));
              }}
            >
              {cat.label}
            </div>
          ))}
        </div>
      )
    }

    // Only CIP tab uses this sub-header — all other tabs (D2D, PRESENT, HOME) show nothing
    return null
  }

  // Render Main workspace Area depending on Active states
  const renderWorkspace = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spin size="large" tip="Loading Compliance Dashboard Details..." />
        </div>
      )
    }

    if (activeTab === 'HOME') {
      return (
        <div>
          <h4 className="mb-3 font-weight-bold" style={{ color: '#333' }}>STANDARD GRAPHS ON HOME PAGES</h4>
          <Row>
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>Duty Saving Trend (USD)</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Chart
                    options={{
                      chart: { toolbar: { show: true } },
                      colors: ['#25DBCD', '#F59E0B'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'dark',
                          type: 'vertical',
                          shadeIntensity: 0.8,
                          gradientToColors: ['#06B6D4', '#EF4444'],
                          inverseColors: false,
                          opacityFrom: 0.85,
                          opacityTo: 0.25,
                          stops: [0, 95, 100]
                        }
                      },
                      stroke: { curve: 'smooth', width: 3 },
                      xaxis: { categories: dutySavingChartCategories },
                      grid: { borderColor: borderGrey }
                    }}
                    series={dutySavingChartSeries}
                    type="area"
                    height="280"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>Obligation Fulfillment Target (USD)</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Chart
                    options={{
                      chart: { toolbar: { show: true } },
                      colors: ['#3B82F6', '#10B981'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.5,
                          gradientToColors: ['#8B5CF6', '#059669'],
                          inverseColors: false,
                          opacityFrom: 1,
                          opacityTo: 0.8,
                          stops: [0, 100]
                        }
                      },
                      plotOptions: { bar: { columnWidth: '45%', borderRadius: 6 } },
                      xaxis: { categories: ['Total Targets', 'Total Realised'] },
                      grid: { borderColor: borderGrey }
                    }}
                    series={[
                      { name: 'Target Obligation', data: [Math.round(homeMetrics.targetObligation), 0] },
                      { name: 'Fulfillment Actual', data: [0, Math.round(homeMetrics.fulfillmentActual)] }
                    ]}
                    type="bar"
                    height="280"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>Overall Compliance Rate</Card.Header.Title></Card.Header>
                <Card.Body className="text-center">
                  <Chart
                    options={{
                      colors: ['#F59E0B'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'dark',
                          type: 'horizontal',
                          shadeIntensity: 0.7,
                          gradientToColors: ['#10B981'],
                          inverseColors: true,
                          opacityFrom: 1,
                          opacityTo: 1,
                          stops: [0, 100]
                        }
                      },
                      plotOptions: {
                        radialBar: {
                          hollow: { size: '65%' },
                          track: { background: '#F1F5F9', strokeWidth: '100%' },
                          dataLabels: {
                            name: { show: true, fontSize: '12px', color: '#64748B', offsetY: -10 },
                            value: { fontSize: '22px', fontWeight: 'bold', color: '#0F172A', offsetY: 4 }
                          }
                        }
                      },
                      labels: ['Compliance Rate']
                    }}
                    series={[Math.round(homeMetrics.complianceRate)]}
                    type="radialBar"
                    height="200"
                  />
                  <p className="mt-2 text-muted" style={{ fontSize: '13px' }}>
                    {Math.round(homeMetrics.complianceRate)}% of shipping bills cleared BRC successfully
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={8} className="mb-4">
              <Card style={{ height: '100%' }}>
                <Card.Header><Card.Header.Title>Quick Financial Metrics Summary</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Table bordered hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Metric Type</th>
                        <th className="text-end">Value (INR)</th>
                        <th className="text-end">Value (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="font-weight-bold">Total Import CIF value</td>
                        <td className="text-end font-weight-bold">{formatCurrency(homeMetrics.importDutySaved, 'INR')}</td>
                        <td className="text-end font-weight-bold">{formatCurrency(homeMetrics.importDutySaved / 83.5, 'USD')}</td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold">Total Export FOB realized value</td>
                        <td className="text-end font-weight-bold text-success">{formatCurrency(homeMetrics.exportRealization, 'INR')}</td>
                        <td className="text-end font-weight-bold text-success">{formatCurrency(homeMetrics.exportRealization / 83.5, 'USD')}</td>
                      </tr>
                      <tr>
                        <td>Advance Auth + EPCG Target Obligations</td>
                        <td className="text-end">{formatCurrency(homeMetrics.targetObligation * 83.5, 'INR')}</td>
                        <td className="text-end">{formatCurrency(homeMetrics.targetObligation, 'USD')}</td>
                      </tr>
                      <tr>
                        <td>Advance Auth + EPCG Realised Obligations</td>
                        <td className="text-end">{formatCurrency(homeMetrics.fulfillmentActual * 83.5, 'INR')}</td>
                        <td className="text-end">{formatCurrency(homeMetrics.fulfillmentActual, 'USD')}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      )
    }

    if (activeTab === 'CIP') {
      const subTabs = categorySubTabs[selectedCategory] || [];

      return (
        <div>
          {/* Sub-tab selection bar inside workspace — heading + pills on one line */}
          {subTabs.length > 1 && (
            <div className="cip-subtabs-bar">
              <div className="cip-subtabs-heading">
                <span className="cip-subtabs-title">{selectedCategory}</span>
                <span className="cip-subtabs-sub">
                  {(subTabs.find(t => t.key === selectedSubTab)?.label) || 'Select a view'}
                </span>
              </div>
              <div className="cip-subtabs" role="tablist" aria-label="Compliance sub-sections">
                {subTabs.map((tab) => {
                  const isActive = selectedSubTab === tab.key;
                  // Disable only if restricted in user's RBAC mapping
                  const isDisabled = allowedComplianceTabs !== null && !allowedComplianceTabs.has(tab.key);
                  const targetRoute = subTabRoutes[tab.key];
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        if (targetRoute) {
                          const serializedDates = dates?.length === 2 ? [dayjs(dates[0]).format('YYYY-MM-DD'), dayjs(dates[1]).format('YYYY-MM-DD')] : null;
                          navigate(targetRoute, { state: { passedDates: serializedDates } });
                        } else {
                          setSelectedSubTab(tab.key);
                        }
                      }}
                      title={isDisabled ? 'Access restricted' : targetRoute ? `Go to ${tab.label} Report` : tab.label}
                      className={`cip-subtab ${isActive ? 'is-active' : ''} ${isDisabled ? 'is-disabled' : ''}`}
                    >
                      {tab.label} {targetRoute && <i className="fa-solid fa-arrow-up-right-from-square ext"></i>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isComparing && !isCompareActive && (
            <div className="cip-compare-hint">Compare isn't available for this report yet, so it shows {selectedIECName || 'one company'} only.</div>
          )}

          {/* Sub-tab content area */}
          {selectedSubTab === 'Licenses-Detail' ? (
            <Row>
              {/* Left side list of licenses */}
              <Col md={3}>
                <Card style={{ border: `1px solid ${borderGrey}` }}>
                  <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: `1px solid ${borderGrey}` }}>
                    <h6 className="mb-0 font-weight-bold">Authorization and Bonds</h6>
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    <div className="list-group list-group-flush">
                      {licenseOptions.length === 0 ? (
                        <div className="p-3 text-muted text-center" style={{ fontSize: '12px' }}>No active licenses found</div>
                      ) : (
                        licenseOptions.map((lic) => (
                          <button
                            key={lic}
                            onClick={() => setSelectedLicense(lic)}
                            className="list-group-item list-group-item-action border-0"
                            style={{
                              backgroundColor: selectedLicense === lic ? brandGold : 'white',
                              color: selectedLicense === lic ? '#fff' : '#495057',
                              fontWeight: selectedLicense === lic ? '600' : '500',
                              padding: '12px 20px',
                              borderLeft: selectedLicense === lic ? '4px solid #25DBCD' : 'none',
                              fontSize: '12px'
                            }}
                          >
                            Lic: {lic}
                          </button>
                        ))
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Right side detailed compliance grid sheets */}
              <Col md={9}>
                <Card style={{ border: `1px solid ${borderGrey}` }}>
                  <Card.Header className="d-flex justify-content-between align-items-center flex-wrap" style={{ borderBottom: `1px solid ${borderGrey}` }}>
                    <h5 className="mb-0 font-weight-bold" style={{ fontSize: '14px' }}>Fulfillment Status: {selectedLicense || 'Select License'}</h5>
                    <div className="d-flex gap-2 align-items-center">
                      <Button
                        onClick={() => setActiveLicTab('ExpObg')}
                        className="custom-button"
                        style={{
                          width: 'auto',
                          padding: '0.4rem 0.8rem',
                          fontSize: '11px',
                          opacity: activeLicTab === 'ExpObg' ? 1.0 : 0.5,
                          border: activeLicTab === 'ExpObg' ? '2px solid #25DBCD' : 'none',
                          transition: 'all 0.2s ease',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        size="sm"
                      >
                        Adv. Auto. Exp Obg.
                      </Button>
                      <Button
                        onClick={() => setActiveLicTab('ImpSum')}
                        className="custom-button_row_two"
                        style={{
                          width: 'auto',
                          padding: '0.4rem 0.8rem',
                          fontSize: '11px',
                          opacity: activeLicTab === 'ImpSum' ? 1.0 : 0.5,
                          border: activeLicTab === 'ImpSum' ? '2px solid #25DBCD' : 'none',
                          transition: 'all 0.2s ease',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        size="sm"
                      >
                        Import Summary
                      </Button>
                      <Button
                        onClick={() => setActiveLicTab('LicDash')}
                        className="custom-button"
                        style={{
                          width: 'auto',
                          padding: '0.4rem 0.8rem',
                          fontSize: '11px',
                          opacity: activeLicTab === 'LicDash' ? 1.0 : 0.5,
                          border: activeLicTab === 'LicDash' ? '2px solid #25DBCD' : 'none',
                          transition: 'all 0.2s ease',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        size="sm"
                      >
                        Dashboard
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    {activeLicTab === 'ExpObg' && (
                      <Table bordered hover responsive size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                        <thead className="table-light">
                          <tr>
                            <th>Shipping Bill</th>
                            <th>Export Product</th>
                            <th className="text-end">Allowed Qty</th>
                            <th className="text-end">Realised FOB ($)</th>
                            <th>BRC Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {licExportData.length === 0 ? (
                            <tr><td colSpan="5" className="text-center text-muted">No export shipments linked to this license</td></tr>
                          ) : (
                            licExportData.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.sb_no || row.shipping_bill_no || '-'}</td>
                                <td>{row.export_product || row.product_exported || '-'}</td>
                                <td className="text-end">{row.export_qty != null ? Number(row.export_qty).toLocaleString() : '-'}</td>
                                <td className="text-end">{formatCurrency(row.total_realised_value_usd, 'USD')}</td>
                                <td>
                                  <Badge bg={row.brc_remark?.includes('Cleared') || row.final_remark?.includes('realized') ? 'success' : 'warning'} text="dark">
                                    {row.brc_remark || row.final_remark || 'PENDING BRC'}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    )}

                    {activeLicTab === 'ImpSum' && (
                      <Table bordered hover responsive size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                        <thead className="table-light">
                          <tr>
                            <th>Bill of Entry (BOE)</th>
                            <th>Import Item</th>
                            <th className="text-end">Import Qty</th>
                            <th className="text-end">CIF Value ($)</th>
                            <th className="text-end">Duty Saved (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {licImportData.length === 0 ? (
                            <tr><td colSpan="5" className="text-center text-muted">No import bills linked to this license</td></tr>
                          ) : (
                            licImportData.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.be_no || '-'}</td>
                                <td>{row.item_imported || '-'}</td>
                                <td className="text-end">{row.qty_imported != null ? Number(row.qty_imported).toLocaleString() : '-'}</td>
                                <td className="text-end">{formatCurrency(row.cif_imported_usd, 'USD')}</td>
                                <td className="text-end text-success">{formatCurrency(row.total_duty_exempted, 'INR')}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    )}

                    {activeLicTab === 'LicDash' && (
                      <div className="text-center p-3">
                        <h6 style={{ fontSize: '13px' }}>Obligation Realization Progress - License {selectedLicense}</h6>
                        <Chart
                          options={{
                            chart: { toolbar: { show: true } },
                            colors: ['#25DBCD', chartAccent],
                            fill: {
                              type: 'gradient',
                              gradient: {
                                shadeIntensity: 1,
                                opacityFrom: 0.8,
                                opacityTo: 0.2,
                                gradientToColors: ['#06b6d4', '#ec4899'],
                                stops: [0, 90, 100]
                              }
                            },
                            stroke: { curve: 'smooth', width: 3 },
                            xaxis: { categories: ['Stage 1', 'Stage 2', 'Stage 3', 'Final Realised'] }
                          }}
                          series={licFulfillmentSeries}
                          type="area"
                          height="220"
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : selectedSubTab === 'IMP-DUTY-RATE' ? (
            renderImpDutyRateSearch()
          ) : selectedSubTab === 'Inconsistency' ? (
            <div>
              <Row className="g-3 align-items-start" style={{ margin: 0 }}>
                <Col md={7} lg={7} xl={7} style={{ paddingRight: '8px' }}>
                  {renderInconsistencyCards()}
                </Col>
                <Col md={5} lg={5} xl={5} style={{ paddingLeft: '8px' }}>
                  {renderSummaryChart('Inconsistency')}
                </Col>
              </Row>
            </div>
          ) : selectedSubTab === 'Duty-Saving-Report' ? (
            <DutySavingSummary
              currency={selectedCurrency}
              data={summaryDashboardData['Duty-Saving-Report']}
              loading={isLoading2}
              error={dutySavingError}
              onRetry={() => getSummaryDashboard('Duty-Saving-Report')}
            />
          ) : selectedSubTab === 'Executive-Consolidated-Audit' ? (
            <ConsolidatedReportView
              embedded={true}
              parentIEC={activeIEC}
              parentDates={dates}
              parentCurrency={selectedCurrency}
            />
          ) : isCompareActive ? (
            renderCompareView(selectedSubTab)
          ) : (
            /* ── KPI band + split layout (summary table + chart) + distribution ── */
            <div>
              {renderKpiBand(selectedSubTab)}
              <Row className="g-3 align-items-start" style={{ margin: 0 }}>
                <Col md={7} style={{ paddingRight: '8px' }}>
                  <div style={{ minHeight: '350px', border: `1px solid ${borderGrey}`, borderRadius: '8px', overflow: 'hidden' }}>
                    {renderAdminSummary(selectedSubTab)}
                  </div>
                </Col>
                <Col md={5} style={{ paddingLeft: '8px' }} className="d-flex flex-column gap-3">
                  {renderSummaryChart(selectedSubTab)}
                </Col>
              </Row>
              <div style={{ marginTop: '16px', width: '100%' }}>
                {renderDistributionStrip(selectedSubTab)}
              </div>
            </div>
          )}
        </div>
      )
    }

    if (activeTab === 'D2D') {
      return (
        <div>
          <h4 className="mb-3 font-weight-bold" style={{ color: '#333' }}>IMP - GRAPHS CREATE/EDIT</h4>
          <Row>
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>Clearance Times (Import vs Export)</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Chart
                    options={{
                      chart: { toolbar: { show: true } },
                      colors: ['#06B6D4', '#F59E0B'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.5,
                          gradientToColors: ['#3B82F6', '#EF4444'],
                          inverseColors: false,
                          opacityFrom: 1,
                          opacityTo: 0.75,
                          stops: [0, 100]
                        }
                      },
                      plotOptions: { bar: { columnWidth: '45%', borderRadius: 6 } },
                      xaxis: { categories: ['Sea Port', 'Air Cargo', 'ICD'] }
                    }}
                    series={clearanceSeries}
                    type="bar"
                    height="240"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>Unrealised e-BRC Count</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Chart
                    options={{
                      chart: { toolbar: { show: true } },
                      colors: ['#EF4444'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'light',
                          type: 'vertical',
                          shadeIntensity: 0.5,
                          gradientToColors: ['#8B5CF6'],
                          inverseColors: false,
                          opacityFrom: 1,
                          opacityTo: 0.75,
                          stops: [0, 100]
                        }
                      },
                      plotOptions: { bar: { columnWidth: '45%', borderRadius: 6 } },
                      xaxis: { categories: ['<240 Days', '240-270 Days', '270+ Days', '120+ Days'] }
                    }}
                    series={brcPendingSeries}
                    type="bar"
                    height="240"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>Obligation Limit (AA vs EPCG Target USD)</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Chart
                    options={{
                      chart: { toolbar: { show: true } },
                      colors: ['#25DBCD', '#10B981'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'dark',
                          type: 'diagonal1',
                          shadeIntensity: 0.6,
                          gradientToColors: ['#EC4899', '#38BDF8'],
                          opacityFrom: 1,
                          opacityTo: 0.85,
                          stops: [0, 100]
                        }
                      },
                      labels: ['Advance Auth Obligation', 'EPCG Obligation']
                    }}
                    series={dutySavingAAEPCGSeries}
                    type="donut"
                    height="240"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header><Card.Header.Title>FTA Customs Duty Saving Opportunity</Card.Header.Title></Card.Header>
                <Card.Body>
                  <Chart
                    options={{
                      chart: { toolbar: { show: true } },
                      colors: ['#EA580C', '#25DBCD'],
                      fill: {
                        type: 'gradient',
                        gradient: {
                          shade: 'dark',
                          type: 'vertical',
                          shadeIntensity: 0.8,
                          gradientToColors: ['#FBBF24', '#EC4899'],
                          inverseColors: false,
                          opacityFrom: 0.8,
                          opacityTo: 0.2,
                          stops: [0, 95, 100]
                        }
                      },
                      stroke: { curve: 'smooth', width: 3 },
                      xaxis: { categories: ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Latest'] }
                    }}
                    series={missedOpportunitySeries}
                    type="area"
                    height="240"
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      )
    }

    if (activeTab === 'PRESENT') {
      return (
        <PresentGraphs
          height={560}
          overrideDateRange={true}
          dateFrom={dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null}
          dateTo={dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null}
        />
      )
    }

    if (activeTab === 'STANDARD') {
      return (
        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Card.Header
            className="d-flex align-items-center justify-content-between flex-wrap gap-2"
            style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #f1f5f9', padding: '16px 24px' }}
          >
            <div>
              <h3 className="std-cert-main-title">
                {selectedStatementType} Standard Data
              </h3>
              <span className="std-cert-subtitle">
                Managing standard compliance records for <b style={{ color: '#0f172a' }}>{selectedStatementType}</b>
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                className="cip-btn-submit"
                style={{
                  fontSize: '15px',
                  padding: '8px 22px',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => setShowCreateCertForm(prev => !prev)}
              >
                {showCreateCertForm ? '✕ Close Form' : `+ Create ${selectedStatementType === 'Returns' ? 'Returns' : 'Certificate'}`}
              </Button>
              <Button
                className="cip-btn-primary"
                style={{ fontSize: '15px', padding: '8px 26px' }}
                onClick={() => message.success(`Exporting ${selectedStatementType} data...`)}
              >
                Export
              </Button>
            </div>
          </Card.Header>
          <Card.Body style={{ padding: '20px 24px', backgroundColor: '#ffffff' }}>
            {/* Expandable Creation Form / Upload Panel */}
            {showCreateCertForm && (
              (selectedStatementType === 'FTA' || selectedStatementType === 'CCR') ? (
                /* FTA & CCR Direct Upload Panel */
                <div
                  className="p-4 mb-4"
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <h5 style={{ fontWeight: '800', color: '#000000', marginBottom: '16px', fontSize: '18px' }}>
                    {selectedStatementType} File Upload
                  </h5>
                  <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setNewCertForm({ ...newCertForm, file: e.target.files[0] })}
                      style={{ fontSize: '13px' }}
                    />
                    <Button
                      className="cip-btn-primary"
                      style={{ fontSize: '13px', padding: '6px 20px' }}
                      onClick={async () => {
                        if (!newCertForm.file) {
                          message.warning('Please select a file to upload.');
                          return;
                        }
                        const token = localStorage.getItem('token');
                        const uploadedFileObj = {
                          key: Date.now().toString(),
                          filename: newCertForm.file.name,
                          user_iec_no: activeIEC || '0388041871',
                          fullname: selectedIECName || 'ALFA LAVAL INDIA PRIVATE LIMITED - ALIPL',
                          status: 'Done',
                          remarks: 'Success',
                          created_at: dayjs().format('DD-MM-YYYY')
                        };

                        if (selectedStatementType === 'CCR') {
                          setCcrFileList(prev => [uploadedFileObj, ...prev]);
                        } else {
                          setFtaFileList(prev => [uploadedFileObj, ...prev]);
                        }

                        try {
                          message.loading({ content: `Uploading ${selectedStatementType} file...`, key: 'file_upload' });
                          await uploadFiles([newCertForm.file], selectedStatementType, activeIEC || '', {});
                          message.success({ content: `${selectedStatementType} file uploaded successfully!`, key: 'file_upload' });
                          setShowCreateCertForm(false);
                          setNewCertForm({ ...newCertForm, file: null });

                          // Refresh status data
                          const uploadRes = await fileUploadStatusDataService(selectedStatementType, token);
                          if (uploadRes && uploadRes.data && uploadRes.data.length > 0) {
                            const mapped = uploadRes.data.map((item, idx) => ({
                              key: item.id || item.file_status_id || idx,
                              filename: item.file_name || item.filename || '-',
                              user_iec_no: item.iec_no || item.user_iec_no || activeIEC || '-',
                              fullname: item.iec_name || selectedIECName || '-',
                              status: item.file_status || item.status || 'Done',
                              remarks: item.remarks || item.remark || 'Success',
                              created_at: item.created_at ? dayjs(item.created_at).format('DD-MM-YYYY') : '-'
                            }));
                            if (selectedStatementType === 'CCR') setCcrFileList(mapped);
                            else setFtaFileList(mapped);
                          }
                        } catch (err) {
                          console.error(`${selectedStatementType} Upload Error:`, err);
                          message.error({ content: `Failed to upload ${selectedStatementType} file to server.`, key: 'file_upload' });
                        }
                      }}
                    >
                      Upload
                    </Button>
                  </div>
                </div>
              ) : selectedStatementType === 'Returns' ? (
                /* Create Returns Form Layout matching reference */
                <div
                  className="p-4 mb-4"
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div className="mb-4">
                    <h5 style={{ fontWeight: '800', color: '#0f172a', margin: 0, fontSize: '18px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      RETURNS
                    </h5>
                    <div style={{ fontSize: '14px', marginTop: '4px' }}>
                      <b style={{ color: '#0f172a', fontWeight: '800' }}>
                        {selectedIECName || 'CUMMINS INDIA LIMITED'}
                      </b>{' '}
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>
                        {activeIEC && activeIEC !== 'ALL' ? activeIEC : (userDetails?.iec || localStorage.getItem('iec_no') || '0388081627')}
                      </span>
                    </div>
                  </div>

                  <BootstrapForm>
                    {/* Row 1: Description* */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="returns_description">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                        Description<span style={{ color: '#ef4444' }}>*</span>
                      </BootstrapForm.Label>
                      <Col sm="9">
                        <BootstrapForm.Control
                          type="text"
                          placeholder="Enter Description"
                          value={newCertForm.description || ''}
                          onChange={(e) => setNewCertForm({ ...newCertForm, description: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#0f172a', fontWeight: '500', borderColor: '#cbd5e1' }}
                        />
                      </Col>
                    </BootstrapForm.Group>

                    {/* Row 2: Return date* & Tenure* */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="returns_date_tenure">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                        Return date<span style={{ color: '#ef4444' }}>*</span>
                      </BootstrapForm.Label>
                      <Col sm="4">
                        <BootstrapForm.Control
                          type="date"
                          placeholder="mm/dd/yyyy"
                          value={newCertForm.return_date || ''}
                          onChange={(e) => setNewCertForm({ ...newCertForm, return_date: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#0f172a', fontWeight: '500', borderColor: '#cbd5e1' }}
                        />
                      </Col>
                      <BootstrapForm.Label column sm="1" className="text-end" style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                        Tenure<span style={{ color: '#ef4444' }}>*</span>
                      </BootstrapForm.Label>
                      <Col sm="4">
                        <BootstrapForm.Select
                          value={newCertForm.tenure || ''}
                          onChange={(e) => setNewCertForm({ ...newCertForm, tenure: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#0f172a', fontWeight: '500', borderColor: '#cbd5e1' }}
                        >
                          <option value="">Select Tenure</option>
                          <option value="One Time">One Time</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Quarterly">Quarterly</option>
                          <option value="Half Yearly">Half Yearly</option>
                          <option value="Yearly">Yearly</option>
                          <option value="2 Yearly">2 Yearly</option>
                          <option value="3 Yearly">3 Yearly</option>
                          <option value="4 Yearly">4 Yearly</option>
                          <option value="5 Yearly">5 Yearly</option>
                          <option value="6 Yearly">6 Yearly</option>
                        </BootstrapForm.Select>
                      </Col>
                    </BootstrapForm.Group>

                    {/* Row 3: Alert trigger rate in days* */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="returns_trigger">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                        Alert trigger rate in days<span style={{ color: '#ef4444' }}>*</span>
                      </BootstrapForm.Label>
                      <Col sm="4">
                        <BootstrapForm.Control
                          type="text"
                          placeholder="Enter Trigger rate"
                          value={newCertForm.trigger_rate || ''}
                          onChange={(e) => setNewCertForm({ ...newCertForm, trigger_rate: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#0f172a', fontWeight: '500', borderColor: '#cbd5e1' }}
                        />
                      </Col>
                    </BootstrapForm.Group>

                    {/* Row 4: Upload docs */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="returns_upload">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                        Upload docs
                      </BootstrapForm.Label>
                      <Col sm="9">
                        <BootstrapForm.Control
                          type="file"
                          onChange={(e) => setNewCertForm({ ...newCertForm, file: e.target.files[0] })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#0f172a', fontWeight: '500', borderColor: '#cbd5e1' }}
                        />
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                          {newCertForm.file ? newCertForm.file.name : 'No files selected'}
                        </div>
                      </Col>
                    </BootstrapForm.Group>

                    {/* Action Button: Submit */}
                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        className="cip-btn-primary"
                        style={{ fontSize: '14px', padding: '8px 32px', background: 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))', border: '1px solid rgba(20, 184, 166, 0.45)', borderRadius: '6px', color: '#000000', letterSpacing: '-0.01em', boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 2px 6px rgba(32, 216, 202, 0.25)' }}
                        onClick={async () => {
                          if (!newCertForm.description || !newCertForm.return_date || !newCertForm.tenure || !newCertForm.trigger_rate) {
                            message.warning('Please fill in all required fields (*)');
                            return;
                          }
                          const token = localStorage.getItem('token');
                          const newReturnRecord = {
                            key: Date.now(),
                            iec_no: activeIEC || '0388081627',
                            iec_name: selectedIECName || 'CUMMINS INDIA LIMITED',
                            file_no: newCertForm.description,
                            issuing_date: newCertForm.return_date,
                            created_at: newCertForm.return_date,
                            expiry_date: newCertForm.return_date,
                            issuing_authority: newCertForm.tenure,
                            purpose: `Trigger: ${newCertForm.trigger_rate} days`
                          };

                          setStdCertData(prev => ({
                            ...prev,
                            Returns: [newReturnRecord, ...(prev.Returns || [])]
                          }));

                          try {
                            message.loading({ content: 'Submitting Returns data...', key: 'returns_submit' });
                            const iecToUse = (activeIEC && activeIEC !== 'ALL') ? activeIEC : (userDetails?.iec || localStorage.getItem('iec_no') || '0388081627');

                            const returnsFormData = new FormData();
                            returnsFormData.append('description', newCertForm.description);
                            returnsFormData.append('return_date', newCertForm.return_date);
                            returnsFormData.append('tenure', newCertForm.tenure);
                            returnsFormData.append('trigger_rate', newCertForm.trigger_rate);
                            returnsFormData.append('return_submitted', 'false');
                            returnsFormData.append('iec_no', iecToUse);
                            if (newCertForm.file) {
                              returnsFormData.append('upload_docs', newCertForm.file);
                            }
                            await submitForm(returnsFormData, token);

                            message.success({ content: 'Returns data submitted successfully!', key: 'returns_submit' });
                            setShowCreateCertForm(false);
                            setNewCertForm({
                              iec_no: '',
                              iec_name: '',
                              file_no: '',
                              issuing_date: '',
                              expiry_date: '',
                              issuing_authority: '',
                              purpose: '',
                              description: '',
                              return_date: '',
                              tenure: '',
                              trigger_rate: '',
                              file: null
                            });
                            if (activeIEC) {
                              fetchDashboardData(activeIEC);
                            }
                          } catch (err) {
                            console.error('Returns Submit Error:', err);
                            message.error({ content: 'Failed to submit Returns data.', key: 'returns_submit' });
                          }
                        }}
                      >
                        Submit
                      </Button>
                    </div>
                  </BootstrapForm>
                </div>
              ) : (
                /* Standard Entry Form (4-Row Executive Layout) */
                <div
                  className="p-4 mb-4"
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div className="mb-4">
                    <h5 style={{ fontWeight: '800', color: '#0f172a', margin: 0, fontSize: '16px' }}>
                      {selectedStatementType}
                    </h5>
                  </div>

                  <BootstrapForm>
                    {/* Row 1: File No.* */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="file_no">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                        File No.*
                      </BootstrapForm.Label>
                      <Col sm="9">
                        <BootstrapForm.Control
                          type="text"
                          placeholder="Enter file no."
                          value={newCertForm.file_no}
                          onChange={(e) => setNewCertForm({ ...newCertForm, file_no: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#000000', fontWeight: '500', borderColor: newCertForm.file_no ? '#cbd5e1' : '#f87171' }}
                        />
                      </Col>
                    </BootstrapForm.Group>

                    {/* Row 2: Issuing Date* & Expiry* */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="dates">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                        Issuing Date*
                      </BootstrapForm.Label>
                      <Col sm="4">
                        <BootstrapForm.Control
                          type="date"
                          value={newCertForm.issuing_date}
                          onChange={(e) => setNewCertForm({ ...newCertForm, issuing_date: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#000000', fontWeight: '500', borderColor: newCertForm.issuing_date ? '#cbd5e1' : '#f87171' }}
                        />
                      </Col>
                      <Col sm="1" className="text-center">
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>Expiry*</span>
                      </Col>
                      <Col sm="4">
                        <BootstrapForm.Control
                          type="date"
                          value={newCertForm.expiry_date}
                          onChange={(e) => setNewCertForm({ ...newCertForm, expiry_date: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#000000', fontWeight: '500', borderColor: newCertForm.expiry_date ? '#cbd5e1' : '#f87171' }}
                        />
                      </Col>
                    </BootstrapForm.Group>

                    {/* Row 3: Issuing Authority* */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="issuing_authority">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                        Issuing Authority*
                      </BootstrapForm.Label>
                      <Col sm="9">
                        <BootstrapForm.Control
                          type="text"
                          placeholder="Enter Issuing Authority"
                          value={newCertForm.issuing_authority}
                          onChange={(e) => setNewCertForm({ ...newCertForm, issuing_authority: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#000000', fontWeight: '500', borderColor: newCertForm.issuing_authority ? '#cbd5e1' : '#f87171' }}
                        />
                      </Col>
                    </BootstrapForm.Group>

                    {/* Row 4: Purpose (optional) */}
                    <BootstrapForm.Group as={Row} className="mb-3 align-items-center" controlId="purpose">
                      <BootstrapForm.Label column sm="3" style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                        Purpose (optional)
                      </BootstrapForm.Label>
                      <Col sm="9">
                        <BootstrapForm.Control
                          type="text"
                          placeholder="Enter purpose"
                          value={newCertForm.purpose}
                          onChange={(e) => setNewCertForm({ ...newCertForm, purpose: e.target.value })}
                          style={{ fontSize: '13px', borderRadius: '4px', color: '#000000', fontWeight: '500', borderColor: '#cbd5e1' }}
                        />
                      </Col>
                    </BootstrapForm.Group>

                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        className="cip-btn-primary"
                        style={{ fontSize: '13px', padding: '6px 28px' }}
                        onClick={async () => {
                          if (!newCertForm.file_no || !newCertForm.issuing_date || !newCertForm.expiry_date || !newCertForm.issuing_authority) {
                            message.warning('Please fill in all required fields (*)');
                            return;
                          }
                          const token = localStorage.getItem('token');
                          const newCertRecord = {
                            key: Date.now(),
                            iec_no: activeIEC || '3106004240',
                            iec_name: selectedIECName || 'ALFA LAVAL INDIA PRIVATE LIMITED',
                            file_no: newCertForm.file_no,
                            issue_date: newCertForm.issuing_date,
                            created_at: newCertForm.issuing_date,
                            expiry_date: newCertForm.expiry_date,
                            issuing_authority: newCertForm.issuing_authority,
                            purpose: newCertForm.purpose
                          };
                          // Immediately update local frontend state so user sees record in table
                          setStdCertData(prev => ({
                            ...prev,
                            [selectedStatementType]: [newCertRecord, ...(prev[selectedStatementType] || [])]
                          }));

                          try {
                            message.loading({ content: `Submitting ${selectedStatementType} data...`, key: 'cert_submit' });

                            const iecToUse = (activeIEC && activeIEC !== 'ALL') ? activeIEC : (userDetails?.iec || localStorage.getItem('iec_no') || '3106004240');
                            const certPayload = {
                              file_no: newCertForm.file_no,
                              issuing_date: newCertForm.issuing_date,
                              expiry_date: newCertForm.expiry_date,
                              issuing_authority: newCertForm.issuing_authority,
                              purpose: newCertForm.purpose || ''
                            };

                            const res = await uploadStdCertificateDataService(selectedStatementType, certPayload, iecToUse, token);
                            if (res && res.data) {
                              message.success({ content: `${selectedStatementType} data submitted successfully!`, key: 'cert_submit' });
                              setShowCreateCertForm(false);
                              setNewCertForm({
                                iec_no: '',
                                iec_name: '',
                                file_no: '',
                                issuing_date: '',
                                expiry_date: '',
                                issuing_authority: '',
                                purpose: '',
                                file: null
                              });
                              if (activeIEC) {
                                fetchDashboardData(activeIEC);
                              }
                            } else {
                              const errMsg = res?.msg || `Failed to submit ${selectedStatementType} data.`;
                              message.error({ content: errMsg, key: 'cert_submit' });
                            }
                          } catch (err) {
                            console.error('Certificate Submit Error:', err);
                            message.error({ content: `Failed to submit ${selectedStatementType} data.`, key: 'cert_submit' });
                          }
                        }}
                      >
                        Submit
                      </Button>
                    </div>
                  </BootstrapForm>
                </div>
              )
            )}

            {/* Segmented Filter Control Bar */}
            <div className="d-flex align-items-center justify-content-between mb-4 pb-3 flex-wrap gap-2" style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div className="d-flex align-items-center gap-2 p-1.5" style={{ backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                {statementsList.map((item) => {
                  const isSelected = selectedStatementType === item.key;
                  let badge = null;
                  const certItems = stdCertData[item.key];
                  const first = certItems && certItems[0];
                  if (first?.expiry_status) {
                    const color = first.color || (first.expiry_status === 'Expired' ? '#ef4444' : first.expiry_status?.includes('Expiring') ? '#f59e0b' : '#10b981');
                    badge = (
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', backgroundColor: `${color}20`, color: color, marginLeft: '6px', border: `1px solid ${color}40` }}>
                        {first.expiry_status}
                      </span>
                    );
                  }

                  let displayTitle = item.title;
                  if (item.key === 'IEC') {
                    displayTitle = `IEC: ${activeIEC === 'ALL' ? (localStorage.getItem('iec_no') || '0388081627') : activeIEC}`;
                  } else if (item.key === 'EH') {
                    displayTitle = `EH${stdCertData['EH']?.[0]?.file_no ? `: ${stdCertData['EH'][0].file_no}` : ''}`;
                  } else if (item.key === 'AEO') {
                    displayTitle = `AEO${stdCertData['AEO']?.[0]?.file_no ? `: ${stdCertData['AEO'][0].file_no}` : ''}`;
                  } else if (item.key === 'RCMC') {
                    displayTitle = `RCMC${stdCertData['RCMC']?.[0]?.file_no ? `: ${stdCertData['RCMC'][0].file_no}` : ''}`;
                  } else if (item.key === 'FSP') {
                    displayTitle = `FSP${stdCertData['FSP']?.[0]?.file_no ? `: ${stdCertData['FSP'][0].file_no}` : ''}`;
                  } else if (item.key === 'DSC') {
                    displayTitle = `DSC${stdCertData['DSC']?.[0]?.file_no ? `: ${stdCertData['DSC'][0].file_no}` : ''}`;
                  } else if (item.key === 'Returns') {
                    const retCert = stdCertData['Returns']?.[0] || stdCertData['RETURNS']?.[0];
                    displayTitle = `Returns${retCert?.file_no ? `: ${retCert.file_no}` : ''}`;
                  }

                  return (
                    <button
                      key={item.key}
                      onClick={() => {
                        if (item.key === 'IEC') {
                          handleShow(item.key);
                        } else {
                          setSelectedStatementType(item.key);
                        }
                      }}
                      style={{
                        backgroundColor: isSelected ? '#ffffff' : 'transparent',
                        color: isSelected ? '#0f172a' : '#64748b',
                        border: isSelected ? '1px solid #cbd5e1' : 'none',
                        fontWeight: isSelected ? '800' : '600',
                        fontSize: '14px',
                        padding: '8px 18px',
                        borderRadius: '6px',
                        boxShadow: isSelected ? '0 2px 4px rgba(0, 0, 0, 0.08)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                    >
                      {displayTitle}
                      {badge}
                    </button>
                  );
                })}
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>
                Viewing: <b style={{ color: '#0f172a', fontWeight: '800' }}>{selectedStatementType}</b>
              </span>
            </div>

            <AntTable
              className="std-cert-table"
              columns={
                (selectedStatementType === 'FTA' || selectedStatementType === 'CCR')
                  ? ftaModalColumns
                  : selectedStatementType === 'Returns'
                    ? returnsModalColumns
                    : certModalColumns
              }
              dataSource={
                selectedStatementType === 'FTA'
                  ? ftaFileList
                  : selectedStatementType === 'CCR'
                    ? ccrFileList
                    : selectedStatementType === 'Returns'
                      ? (returnData && returnData.length > 0
                        ? returnData.map((item, index) => ({
                          key: item.returns_id || item.id || index,
                          returns_id: item.returns_id || item.id,
                          iec_no: item.iec_no || activeIEC || '-',
                          fullname: item.fullname || item.iec_name || selectedIECName || '-',
                          description: item.description || '-',
                          return_date: item.return_date || '-',
                          tenure: item.tenure || '-',
                          trigger_rate: item.trigger_rate || '-',
                          return_submitted: Boolean(item.return_submitted),
                          file_path: item.file_path || [],
                          is_approved: item.is_approved,
                          email_sent_at: item.email_sent_at,
                          rawItem: item
                        }))
                        : ((stdCertData['Returns'] || stdCertData['RETURNS']) && (stdCertData['Returns'] || stdCertData['RETURNS']).length > 0
                          ? (stdCertData['Returns'] || stdCertData['RETURNS']).map((item, index) => ({
                            key: item.key || item.std_certificate_id || index,
                            returns_id: item.std_certificate_id || item.id,
                            std_certificate_id: item.std_certificate_id || item.id,
                            iec_no: item.iec_no || activeIEC || '-',
                            fullname: item.iec_name || item.fullname || selectedIECName || '-',
                            description: item.file_no || item.purpose || item.description || '-',
                            return_date: item.issuing_date || item.issue_date || item.created_at || '-',
                            tenure: item.issuing_authority || item.tenure || '-',
                            trigger_rate: item.purpose || item.trigger_rate || '-',
                            return_submitted: Boolean(item.return_submitted),
                            file_path: item.file_path || [],
                            is_approved: item.is_approved,
                            email_sent_at: item.email_sent_at,
                            rawItem: item
                          }))
                          : []))
                      : ((stdCertData[selectedStatementType] && stdCertData[selectedStatementType].length > 0)
                        ? stdCertData[selectedStatementType].map((item, index) => ({
                          key: item.key || item.std_certificate_id || index,
                          std_certificate_id: item.std_certificate_id || item.id,
                          iec_no: item.iec_no || activeIEC || '-',
                          iec_name: item.iec_name || item.fullname || selectedIECName || '-',
                          file_no: item.file_no || '-',
                          issuing_date: item.issuing_date || item.issue_date || item.created_at || '-',
                          expiry_date: item.expiry_date || '-',
                          issuing_authority: item.issuing_authority || '-',
                          purpose: item.purpose || '',
                          rawItem: item
                        }))
                        : [])
              }
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
              }}
              scroll={{ x: 1100 }}
              size="middle"
              bordered={false}
            />
            {selectedStatementType === 'Returns' ? (
              <ReturnsInputModal
                title="Returns"
                handleClose={handleCloseEditCertModal}
                open={showEditCertModal}
                data={editingCertRecord ? {
                  ...editingCertRecord,
                  returns_id: editingCertRecord.returns_id || editingCertRecord.std_certificate_id || editingCertRecord.id,
                  description: (editingCertRecord.description && editingCertRecord.description !== '-') ? editingCertRecord.description : (editingCertRecord.file_no || editingCertRecord.purpose || ''),
                  return_date: (editingCertRecord.return_date && editingCertRecord.return_date !== '-') ? editingCertRecord.return_date : (editingCertRecord.issuing_date || editingCertRecord.issue_date || ''),
                  tenure: (editingCertRecord.tenure && editingCertRecord.tenure !== '-') ? editingCertRecord.tenure : (editingCertRecord.issuing_authority || ''),
                  trigger_rate: (editingCertRecord.trigger_rate && editingCertRecord.trigger_rate !== '-') ? editingCertRecord.trigger_rate : '',
                  return_submitted: Boolean(editingCertRecord.return_submitted),
                  iec_no: editingCertRecord.iec_no || activeIEC || '',
                  upload_docs: editingCertRecord.upload_docs || editingCertRecord.file_path || []
                } : null}
                tenureOptions={[
                  { value: "One Time", label: "One Time" },
                  { value: "Monthly", label: "Monthly" },
                  { value: "Quarterly", label: "Quarterly" },
                  { value: "Half Yearly", label: "Half Yearly" },
                  { value: "Yearly", label: "Yearly" },
                  { value: "2 Yearly", label: "2 Yearly" },
                  { value: "3 Yearly", label: "3 Yearly" },
                  { value: "4 Yearly", label: "4 Yearly" },
                  { value: "5 Yearly", label: "5 Yearly" },
                  { value: "6 Yearly", label: "6 Yearly" },
                ]}
                IECOptions={Array.isArray(IECOptions) ? IECOptions.map(opt => ({ value: `${opt.iec_no};${opt.fullname || ''}`, label: `${opt.iec_no} (${opt.fullname || ''})` })) : []}
                selectedIEC={activeIEC}
                iecName={selectedIECName}
                errorFunction={(msg) => message.error(msg)}
              />
            ) : (
              <StdCertificateInputModal
                title={selectedStatementType}
                handleClose={handleCloseEditCertModal}
                open={showEditCertModal}
                data={editingCertRecord}
              />
            )}
          </Card.Body>
        </Card>
      )
    }
  }

  return (
    <Fragment>
      <style>{`
        /* Alternate Executive Dashboard - Single Page Viewport Fit */
        .dash-summary-table .ant-table-thead > tr > th {
          font-size: 12px !important;
          font-weight: 700 !important;
          padding: 6px 8px !important;
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
          white-space: normal !important;
          word-break: break-word !important;
          text-align: center !important;
        }
        .dash-summary-table .ant-table-tbody > tr > td {
          font-size: 12.5px !important;
          padding: 5px 8px !important;
          line-height: 1.3 !important;
        }
        .dash-summary-table .ant-table-tbody > tr.summary-bold-row > td {
          font-weight: 800 !important;
          background-color: rgba(241, 245, 249, 0.6) !important;
        }
        .dash-summary-table .ant-table-tbody > tr.summary-bold-row > td span {
          font-weight: 800 !important;
        }
        /* Fit within the card — allow horizontal scroll if wide */
        .dash-summary-wrap { width: 100%; overflow-x: auto; }
        .dash-summary-table,
        .dash-summary-table .ant-table,
        .dash-summary-table .ant-spin-nested-loading,
        .dash-summary-table .ant-spin-container { width: 100% !important; }
        /* First column (Description) may wrap; numeric columns stay on one line */
        .dash-summary-table .ant-table-cell { white-space: normal !important; }
        .dash-summary-table .ant-table-tbody > tr > td:not(:first-child) {
          white-space: nowrap !important;
          text-align: center !important;
        }
        /* Description (first column) left-aligned — header and values; every other column centred */
        .dash-summary-table .ant-table-thead > tr:first-child > th:first-child,
        .dash-summary-table .ant-table-tbody > tr > td:first-child {
          text-align: left !important;
        }
        /* Theme polish: horizontal row separators, zebra, indigo hover */
        .dash-summary-table .ant-table-cell {
          border-color: var(--cip-border) !important;
        }
        .dash-summary-table .ant-table-thead > tr > th::before { display: none !important; }
        .dash-summary-table .ant-table-tbody > tr:nth-child(even) > td {
          background: var(--cip-surface-alt) !important;
        }
        .dash-summary-table .ant-table-tbody > tr:hover > td {
          background: var(--cip-indigo-50) !important;
        }
        .std-cert-table .ant-table-thead > tr > th {
          font-size: 15px !important;
          font-weight: 800 !important;
          padding: 10px 14px !important;
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .std-cert-table .ant-table-tbody > tr > td {
          font-size: 15px !important;
          padding: 10px 14px !important;
          line-height: 1.4 !important;
        }
        .std-cert-main-title {
          font-size: 18px !important;
          font-weight: 800 !important;
          color: #0f172a !important;
          margin: 0 0 4px 0 !important;
          line-height: 1.2 !important;
        }
        .std-cert-subtitle {
          font-size: 15px !important;
          color: #64748b !important;
        }
        .card-header-title, .card-title, h4, h5 {
          font-size: 15px !important;
          font-weight: 700 !important;
        }
        .custom-button_row_two, .custom-button {
          font-size: 14px !important;
          font-weight: 700 !important;
        }
        .ant-radio-button-wrapper {
          font-size: 13px !important;
          height: 32px !important;
          line-height: 30px !important;
          padding: 0 12px !important;
        }
        .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
          font-size: 13px !important;
          height: 32px !important;
        }
        .ant-select-selection-item, .ant-select-selection-placeholder {
          line-height: 30px !important;
          font-size: 13px !important;
        }
        .ant-picker {
          font-size: 13px !important;
          height: 32px !important;
        }
        .ant-picker-input > input {
          font-size: 13px !important;
        }
        .table, table, .table th, .table td {
          font-size: 13px !important;
          padding: 6px 10px !important;
        }

        /* Consistent UI Styling: Standardized Button & Control Corners (Zero boxy elements) */
        .btn, .btn-primary, .btn-secondary, .btn-outline-primary, .btn-outline-secondary, 
        .custom-button, .custom-button_row_two, .ant-btn {
          border-radius: 8px !important;
        }
        .ant-radio-button-wrapper {
          border-radius: 8px !important;
          margin-right: 4px !important;
          border-left: 1px solid #d9d9d9 !important;
        }
        .ant-radio-group-solid .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          border-radius: 8px !important;
        }
        .ant-radio-group {
          display: inline-flex !important;
          gap: 4px !important;
        }
        .ant-radio-button-wrapper:first-child {
          border-radius: 8px !important;
        }
        .ant-radio-button-wrapper:last-child {
          border-radius: 8px !important;
        }
        .ant-select-selector, .ant-select-single .ant-select-selector {
          border-radius: 8px !important;
        }
        .ant-picker, .ant-picker-range {
          border-radius: 8px !important;
        }
        .card, .ant-card {
          border-radius: 12px !important;
        }
        .badge {
          font-size: 12px !important;
          padding: 4px 10px !important;
        }

        /* ─── Meridian cohesive polish (indigo-led, token-driven, dark-aware) ─── */
        .card {
          border: 1px solid var(--cip-border) !important;
          border-radius: 12px !important;
          box-shadow: var(--cip-shadow-sm) !important;
        }
        .card .card-header {
          background: var(--cip-surface-alt) !important;
          border-bottom: 1px solid var(--cip-border) !important;
          font-weight: 700 !important;
        }
        /* Table headers + rows */
        .dash-summary-table .ant-table-thead > tr > th,
        .std-cert-table .ant-table-thead > tr > th,
        .report-summary-table .ant-table-thead > tr > th,
        .ant-table-thead > tr > th {
          background-color: var(--cip-slate-100) !important;
          color: var(--cip-ink) !important;
          border-bottom: 1px solid var(--cip-border) !important;
          font-weight: 700 !important;
        }
        .ant-table-tbody > tr > td { border-bottom: 1px solid var(--cip-border) !important; }
        .ant-table-tbody > tr:hover > td { background: var(--cip-indigo-50) !important; }
        /* Status badges → pills */
        .badge {
          border-radius: 999px !important;
          font-weight: 600 !important;
          letter-spacing: .01em;
        }
        /* Currency / mode segmented radios → indigo */
        .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          background: var(--cip-indigo) !important;
          border-color: var(--cip-indigo) !important;
          box-shadow: -1px 0 0 0 var(--cip-indigo) !important;
        }
        /* These render as separate rounded pills (own border + gap), so antd's built-in
           separator line between buttons is unwanted — it showed as a stray "|" at the
           start of a button. Hide it everywhere. */
        .ant-radio-button-wrapper::before {
          display: none !important;
        }
        /* Hover indigo only on UNchecked pills; the checked pill has an indigo background,
           so its text must stay white on hover (otherwise indigo-on-indigo = invisible). */
        .ant-radio-button-wrapper:not(.ant-radio-button-wrapper-checked):hover {
          color: var(--cip-indigo) !important;
        }
        .ant-radio-button-wrapper-checked:hover { color: #fff !important; }
        /* Chart "Switch chart" selector — flex-centre its content so the text lines up
           vertically with the card title (the global 32px height override otherwise left
           the placeholder sitting low). */
        .cip-chart-switch.ant-select-single .ant-select-selector {
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
        }
        .cip-chart-switch.ant-select .ant-select-selection-placeholder,
        .cip-chart-switch.ant-select .ant-select-selection-item {
          line-height: 30px !important;
          display: flex !important;
          align-items: center !important;
        }
        /* Inputs / selects / pickers → token border + indigo focus ring */
        .ant-select-selector, .ant-picker, .ant-input, .ant-input-affix-wrapper {
          border-radius: 8px !important;
          border-color: var(--cip-border) !important;
        }
        .ant-select-focused .ant-select-selector,
        .ant-picker-focused,
        .ant-input:focus,
        .ant-input-affix-wrapper-focused {
          border-color: var(--cip-indigo) !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,.12) !important;
        }
        /* Workspace container */
        .cip-workspace {
          background: var(--cip-surface) !important;
          border: 1px solid var(--cip-border) !important;
          border-radius: 16px !important;
          box-shadow: var(--cip-shadow-md) !important;
          padding: 14px 16px !important;
        }
        /* Page title */
        .cip-page-title {
          font-size: 19px !important;
          font-weight: 800 !important;
          letter-spacing: -.02em !important;
          color: var(--cip-heading) !important;
          margin: 0 !important;
        }
        .cip-page-eyebrow {
          font-size: 11px; font-weight: 700; letter-spacing: .14em;
          text-transform: uppercase; color: var(--cip-indigo);
          margin: 0 0 1px 0;
        }
        /* Primary CTA (Download Consolidate Report, etc.) */
        .cip-btn-primary, .cip-btn-primary:focus, .cip-btn-primary:active {
          background: var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%)) !important;
          border: 1px solid rgba(20, 184, 166, 0.45) !important;
          color: #000000 !important;
          border-radius: 8px !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          letter-spacing: -0.01em !important;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 2px 6px rgba(32, 216, 202, 0.25) !important;
          transition: all 0.2s ease !important;
        }
        .cip-btn-primary:hover:not(:disabled) {
          filter: brightness(1.06) !important;
          color: #000000 !important;
        }
        .cip-btn-primary:disabled { opacity: .6 !important; }

        /* Left-hand side main vertical tabs navigation */
        .cip-vertical-nav {
          display: flex;
          flex-direction: column;
          gap: 6px;
          background: var(--cip-surface, #ffffff);
          border: 1px solid var(--cip-border, #e2e8f0);
          border-radius: 12px;
          padding: 10px;
          box-shadow: var(--cip-shadow-sm, 0 1px 3px rgba(0,0,0,0.05));
          margin-bottom: 12px;
        }
        .cip-vertical-tab {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 8px;
          border: 1px solid transparent;
          background: transparent;
          color: #475569;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          width: 100%;
        }
        .cip-vertical-tab:hover {
          background: #f1f5f9;
          color: #1e293b;
        }
        .cip-vertical-tab.is-active {
          background: var(--cip-grad-indigo, linear-gradient(135deg, #25dbcd 0%, #0d9488 100%));
          color: #ffffff;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(37, 219, 205, 0.25);
        }
        .cip-vertical-tab__icon {
          font-size: 15px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      {/* Integrated Header Toolbar */}
      <div
        className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 px-3 py-2"
        style={{
          backgroundColor: 'var(--cip-surface, #ffffff)',
          border: '1px solid var(--cip-border, #e2e8f0)',
          borderRadius: '12px',
          boxShadow: 'var(--cip-shadow-sm, 0 1px 3px rgba(0,0,0,0.05))'
        }}
      >
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <h4 className="cip-page-title mb-0" style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
            Compliance Overview
          </h4>

          {(userDetails.role === 'superadmin' || userDetails.role === 'group_head') && (
            <div className="d-flex align-items-center gap-2 ps-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
              <span className="text-secondary" style={{ fontSize: '12.5px', fontWeight: '600' }}>Select Company:</span>
              {activeTab === 'CIP' && canCompareIecs ? (
                // Compliance tab: pick several companies to compare them side by side.
                // None = All Companies, one = that company's own view, two or more = compare.
                <Select
                  mode="multiple"
                  allowClear
                  value={selectedCompanyIecs}
                  onChange={handleCompanySelection}
                  placeholder="All Companies"
                  popupClassName="cip-select-dropdown"
                  optionLabelProp="label"
                  filterOption={(input, option) =>
                    `${option?.label ?? ''} ${option?.children ?? ''} ${option?.value ?? ''}`.toLowerCase().includes(input.toLowerCase())
                  }
                  className="cip-company-select"
                  maxTagCount={3}
                  maxTagPlaceholder={(omitted) => `+${omitted.length}`}
                  // tagRender also draws the "+N" overflow item (not closable), styled as a neutral pill.
                  tagRender={({ label, closable, onClose }) => (
                    <span
                      className={`cip-company-tag${closable ? '' : ' cip-company-tag--more'}`}
                      title={typeof label === 'string' ? label : undefined}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <span className="cip-company-tag__text">{label}</span>
                      {closable && (
                        <CloseOutlined className="cip-company-tag__close" onClick={onClose} aria-label={`Remove ${label}`} />
                      )}
                    </span>
                  )}
                  style={{ minWidth: 230, maxWidth: 400, textAlign: 'left' }}
                  size="middle"
                >
                  {IECOptions.map(opt => (
                    <Select.Option
                      key={opt.iec_no}
                      value={opt.iec_no}
                      label={selectedCompanyIecs.length > 1 ? shortIecName(opt.fullname, opt.iec_no) : (opt.fullname || opt.iec_no)}
                    >
                      {opt.fullname || opt.iec_no}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <Select
                  value={activeIEC}
                  onChange={handleIECChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  popupClassName="cip-select-dropdown"
                  placeholder="Search company…"
                  style={{ width: 230, textAlign: 'left' }}
                  size="middle"
                >
                  {(userDetails.role === 'superadmin' || userDetails.role === 'group_head') && (
                    <Select.Option value="ALL">All Companies</Select.Option>
                  )}
                  {IECOptions.map(opt => (
                    <Select.Option key={opt.iec_no} value={opt.iec_no}>{opt.fullname || opt.iec_no}</Select.Option>
                  ))}
                </Select>
              )}
            </div>
          )}

          {(activeTab === 'CIP' || activeTab === 'PRESENT') && (
            <div className="d-flex align-items-center gap-2 ps-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
              {renderDateFilter()}
            </div>
          )}

          {activeTab === 'D2D' && (
            <div className="d-flex align-items-center gap-2 ps-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
              <span className="font-weight-bold text-dark" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>D2D Intelligence</span>
              {renderDateFilter()}
            </div>
          )}

          {activeTab === 'HOME' && (
            <div className="d-flex align-items-center gap-2 ps-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate('/dashboard/user-management')}
                style={{ borderRadius: '6px', fontSize: '12px', borderColor: '#ccc', color: '#4b5563' }}
              >
                <UserOutlined /> Manage Users
              </Button>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {renderCurrencyToggle()}
          <Tooltip title="Switch between the Classic and New dashboard — you can switch back anytime">
            <Segmented
              className="cip-dash-switch ms-2"
              size="middle"
              value="new"
              onChange={(val) => {
                if (val === 'classic') {
                  localStorage.setItem('preferredDashboard', 'classic');
                  navigate('/dashboard/alternate-dashboard');
                }
              }}
              options={[
                { label: 'Classic', value: 'classic' },
                { label: 'New', value: 'new' },
              ]}
            />
          </Tooltip>
        </div>
      </div>

      <Row>
        <Col md={12}>
          {/* Subheader status bar row */}
          {renderSubHeader()}

          {/* Main changing workspace */}
          <div className="cip-workspace">
            {renderWorkspace()}
          </div>
        </Col>
      </Row>

      {/* Room under the last card so it can scroll clear of the floating Consultation button. */}
      <div className="cip-fab-spacer" aria-hidden="true" />

      {/* Floating Consultation / Query button */}
      <button
        type="button"
        className="cip-fab"
        onClick={() => handleShow("Consultation")}
        title="Raise a query / request consultation"
        aria-label="Raise a query or request consultation"
      >
        <MessageOutlined className="cip-fab__icon" />
        <span className="cip-fab__label">Consultation</span>
      </button>

      {/* Information Modals */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType} Information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalType === "Consultation" ? (
            <BootstrapForm onSubmit={handleConsultSubmit}>
              <BootstrapForm.Group className="mb-2">
                <BootstrapForm.Label>Name</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  name="name"
                  value={consultationForm.name}
                  onChange={handleConsultChange}
                  required
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-2">
                <BootstrapForm.Label>Email</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="email"
                  name="email"
                  value={consultationForm.email}
                  onChange={handleConsultChange}
                  required
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="col-md-12 form-group mb-2">
                <BootstrapForm.Label>Contact No</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  name="contact_no"
                  value={consultationForm.contact_no}
                  onChange={handleConsultChange}
                  required
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-2">
                <BootstrapForm.Label>Subject</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  name="subject"
                  value={consultationForm.subject}
                  onChange={handleConsultChange}
                  required
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-2">
                <BootstrapForm.Label>Message</BootstrapForm.Label>
                <BootstrapForm.Control
                  as="textarea"
                  rows={3}
                  name="message"
                  value={consultationForm.message}
                  onChange={handleConsultChange}
                  required
                />
              </BootstrapForm.Group>

              <Button variant="primary" type="submit" className="mt-2 w-100">
                Send Consultation Request
              </Button>
            </BootstrapForm>
          ) : modalType === "IEC" ? (
            <>
              <p>IEC: {activeIEC && activeIEC !== 'ALL' ? activeIEC : (userDetails.iec || '-')}</p>
              <p>Name: {activeIEC && activeIEC !== 'ALL' ? (IECOptions.find(opt => opt.iec_no === activeIEC)?.fullname || selectedIECName) : userDetails.name}</p>
              <p>Expiry Date: {expiryDate}</p>
              <p>Days left until expiry: {daysLeft}</p>
            </>
          ) : (
            <>
              {stdCertData[modalType] && stdCertData[modalType].length > 0 ? (
                <>
                  <p>File No: <b>{stdCertData[modalType][0]?.file_no || 'N/A'}</b></p>
                  <p>Expiry Date: {stdCertData[modalType][0]?.expiry_date || 'N/A'}</p>
                  <p>Status: <b style={{ color: stdCertData[modalType][0]?.color || 'inherit' }}>
                    {stdCertData[modalType][0]?.expiry_status || 'N/A'}
                  </b></p>
                </>
              ) : (
                <p>No {modalType} data available</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {/* Footer */}
        </Modal.Footer>
      </Modal>

      {/* Consolidated Executive Report Full Excel Data Preview Modal */}
      <Modal
        show={showConsolidatedModal}
        onHide={() => setShowConsolidatedModal(false)}
        size="xl"
        centered
        dialogClassName="cip-excel-preview-modal"
        style={{ zIndex: 1065 }}
      >
        <Modal.Header closeButton style={{ backgroundColor: '#0f172a', color: '#ffffff', borderBottom: '1px solid #1e293b', padding: '14px 20px' }}>
          <Modal.Title style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ backgroundColor: '#1e293b', borderRadius: '6px', padding: '6px 10px', display: 'flex', alignItems: 'center', color: '#38bdf8' }}>
              <i className="fa-solid fa-chart-pie" style={{ fontSize: '16px' }}></i>
            </div>
            <div>
              <div style={{ fontSize: '16px', lineHeight: 1.2, fontWeight: 700 }}>Executive Consolidated Audit Report</div>
              <div style={{ fontSize: '11.5px', color: '#94a3b8', fontWeight: 400, marginTop: '2px' }}>
                IEC: {activeIEC === 'ALL' ? 'All Companies' : activeIEC} | Currency: {selectedCurrency} | Date Range: {dates && dates[0] && dates[1] ? `${dates[0].format('DD/MM/YYYY')} - ${dates[1].format('DD/MM/YYYY')}` : 'Current Financial Year'}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px', backgroundColor: '#f8fafc', minHeight: '480px', maxHeight: '78vh', overflowY: 'auto' }}>
          {/* Direct 1:1 Excel Spreadsheet Grid */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '4px', overflow: 'hidden' }}>
            {renderExactExcelSummarySheet()}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between', padding: '12px 20px' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowConsolidatedModal(false)} style={{ borderRadius: '6px', fontWeight: 600 }}>
            Close Preview
          </Button>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="success"
              size="sm"
              onClick={async () => {
                await handleDownloadConsolidatedReport();
                setShowConsolidatedModal(false);
              }}
              disabled={downloadingReport}
              style={{ borderRadius: '6px', fontWeight: 700, padding: '7px 20px', background: 'var(--cip-grad-indigo, linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%))', border: '1px solid rgba(20, 184, 166, 0.45)', color: '#000000', display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '-0.01em', boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 2px 6px rgba(32, 216, 202, 0.25)' }}
            >
              <i className="fa-solid fa-file-excel" style={{ fontSize: '14px' }}></i>
              <span>{downloadingReport ? 'Generating Report...' : 'Download Full Excel Report (.xlsx)'}</span>
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Fragment>
  )
})

AlternateDashboard.displayName = "AlternateDashboard"
export default AlternateDashboard
